import React, { useState, useMemo, useEffect, useRef } from 'react';
import Page from '../components/Page';
import { 
  useDayBook, 
  useActiveShift, 
  useShiftsList, 
  openShift, 
  closeShift, 
  useCurrentRegister, 
  useRegisterByDate 
} from '../controllers/financial.controller';
import { useStoreSettings, usePrintSettings, useCurrencies } from '../controllers/settings.controller';
import { getInvoiceOrders } from '../controllers/invoices.controller';
import { printReceipt } from '../helpers/PrinterHelper';
import { setDetailsForReceiptPrint } from '../helpers/ReceiptHelper';
import FinancialReportsPrintModal from '../components/FinancialReportsPrintModal';
import { getUserDetailsInLocalStorage } from '../helpers/UserDetails';
import { 
  IconEye, 
  IconPrinter, 
  IconFileSpreadsheet, 
  IconFileText, 
  IconRefresh, 
  IconChevronLeft, 
  IconChevronRight, 
  IconCalendar,
  IconSearch,
  IconX,
  IconCheck,
  IconCoins,
  IconArrowUpRight,
  IconArrowDownRight,
  IconBuildingBank,
  IconArrowLeft,
  IconReceipt,
  IconPlayerPlay,
  IconPlayerStop,
  IconCash,
  IconClock,
  IconUser,
  IconCalculator,
  IconAlertTriangle,
  IconPlus,
  IconMinus,
  IconExchange,
  IconCurrencyDollar,
  IconFilter,
  IconBuildingStore,
  IconBed
} from '@tabler/icons-react';
import { toast } from 'react-hot-toast';
import apiClient from '../helpers/ApiClient';

const DEFAULT_CURRENCIES = [
  { code: 'NPR', symbol: 'Rs.', name: 'Nepalese Rupee', is_base: true, exchange_rate: 1.0, denominations: [1000, 500, 100, 50, 20, 10, 5, 2, 1] },
  { code: 'USD', symbol: '$', name: 'US Dollar', is_base: false, exchange_rate: 135.0, denominations: [100, 50, 20, 10, 5, 2, 1] },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', is_base: false, exchange_rate: 1.6, denominations: [500, 200, 100, 50, 20, 10, 5, 2, 1] }
];

export default function FinancialDayBookPage() {
  // Navigation tabs: 'daily' | 'range' | 'shifts' | 'registers' | 'transactions'
  const [activeTab, setActiveTab] = useState('daily');
  
  // Date states for Day Book
  const [filterType, setFilterType] = useState('today'); // 'today' | 'yesterday' | 'custom' | 'range'
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // Transaction view & drilldown states
  const [drillModal, setDrillModal] = useState({ isOpen: false, title: '', type: '', items: [] });
  const [drillSearch, setDrillSearch] = useState('');
  const [txFilter, setTxFilter] = useState('all');
  const [txFilterTitle, setTxFilterTitle] = useState('All Financial Transactions');
  const [txSearch, setTxSearch] = useState('');

  // Invoice detailed breakdown modal
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  // Opening balance edit modal
  const [isOpeningModalOpen, setIsOpeningModalOpen] = useState(false);
  const [newOpeningBalance, setNewOpeningBalance] = useState('');
  const [isSavingOB, setIsSavingOB] = useState(false);

  // Professional Printable Financial Report Modal State
  const [printModal, setPrintModal] = useState({
    isOpen: false,
    reportType: 'sales', // 'sales' | 'purchase' | 'expenses' | 'register' | 'shift_open' | 'shift_close' | 'shift_full'
    selectedShift: null,
    selectedRegister: null,
    dateRange: { from: fromDate, to: toDate }
  });
  const [isPrintDropdownOpen, setIsPrintDropdownOpen] = useState(false);
  const printDropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (printDropdownRef.current && !printDropdownRef.current.contains(event.target)) {
        setIsPrintDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openPrintReport = (reportType = 'sales', customShift = null, customRegister = null) => {
    setIsPrintDropdownOpen(false);
    setPrintModal({
      isOpen: true,
      reportType,
      selectedShift: customShift || activeShift,
      selectedRegister: customRegister || registerData,
      dateRange: { from: fromDate, to: toDate }
    });
  };

  // Queries
  const { data, isLoading, isError, mutate } = useDayBook(fromDate, toDate);
  const { shift: activeShift, summary: shiftSummary, isLoading: isLoadingShift, mutate: mutateShift } = useActiveShift();
  const { shifts: closedShifts, isLoading: isLoadingClosedShifts, mutate: mutateClosedShifts } = useShiftsList();
  const { data: storeSettings } = useStoreSettings();
  const { data: printSettings } = usePrintSettings();
  const { currencies: fetchedCurrencies } = useCurrencies();

  // Register state
  const [selectedRegisterDate, setSelectedRegisterDate] = useState(() => fromDate);
  const { data: currentRegisterData, isLoading: isLoadingCurrentReg, mutate: mutateCurrentReg } = useCurrentRegister();
  const { data: byDateRegisterData, isLoading: isLoadingByDateReg, mutate: mutateByDateReg } = useRegisterByDate(selectedRegisterDate);
  const registerData = selectedRegisterDate === fromDate && currentRegisterData ? currentRegisterData : byDateRegisterData;

  // Shift modals state
  const [isStartShiftModalOpen, setIsStartShiftModalOpen] = useState(false);
  const [isCloseShiftModalOpen, setIsCloseShiftModalOpen] = useState(false);
  const [isStartingShift, setIsStartingShift] = useState(false);
  const [isClosingShift, setIsClosingShift] = useState(false);
  const [shiftStartFloat, setShiftStartFloat] = useState('');
  const [shiftStartNote, setShiftStartNote] = useState('');
  const [shiftCloseCash, setShiftCloseCash] = useState('');
  const [shiftCloseNote, setShiftCloseNote] = useState('');
  const [selectedShiftForDetails, setSelectedShiftForDetails] = useState(null);

  // Tab mode for modals (Quick entry vs Denomination counting)
  const [startShiftTab, setStartShiftTab] = useState('quick'); // 'quick' | 'denominations'
  const [closeShiftTab, setCloseShiftTab] = useState('quick'); // 'quick' | 'denominations'
  const [openingModalTab, setOpeningModalTab] = useState('quick'); // 'quick' | 'denominations'

  // Certified Staff Profile Name
  const userDetails = getUserDetailsInLocalStorage();
  const staffProfileName = userDetails?.name || userDetails?.username || 'Cashier';

  // Multi-Currency & Denominations Drawer state
  const [isDenomDrawerOpen, setIsDenomDrawerOpen] = useState(false);
  const [denomTarget, setDenomTarget] = useState('general'); // 'general' | 'start_shift' | 'close_shift' | 'opening_balance'
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState('');
  const [customRates, setCustomRates] = useState({});
  const [denominationCounts, setDenominationCounts] = useState({});

  const store = storeSettings || data?.storeInfo || {};
  const storeName = store?.store_name || store?.name || "Restaurant";
  const storeAddress = store?.address || store?.store_address || "";
  const storePhone = store?.phone || store?.store_phone || "";
  const storePan = store?.pan_no || store?.tax_number || store?.pan || "";
  const currency = store?.currency || "NPR";

  // Build available currencies with clean denominations
  const availableCurrencies = useMemo(() => {
    if (fetchedCurrencies && fetchedCurrencies.length > 0) {
      return fetchedCurrencies.map(c => {
        let denoms = Array.isArray(c.denominations) ? c.denominations : [];
        if (typeof c.denominations === 'string') {
          try { denoms = JSON.parse(c.denominations); } catch { denoms = []; }
        }
        if (!denoms || denoms.length === 0) {
          if (c.code === 'USD') denoms = [100, 50, 20, 10, 5, 2, 1];
          else if (c.code === 'INR') denoms = [500, 200, 100, 50, 20, 10, 5, 2, 1];
          else if (c.code === 'EUR') denoms = [500, 200, 100, 50, 20, 10, 5, 2, 1];
          else if (c.code === 'GBP') denoms = [50, 20, 10, 5, 2, 1];
          else denoms = [1000, 500, 100, 50, 20, 10, 5, 2, 1];
        }
        return {
          ...c,
          denominations: [...denoms].sort((a, b) => Number(b) - Number(a))
        };
      });
    }
    return DEFAULT_CURRENCIES;
  }, [fetchedCurrencies]);

  // Active currency in denomination calculator
  const activeCurrency = useMemo(() => {
    if (!availableCurrencies || availableCurrencies.length === 0) return DEFAULT_CURRENCIES[0];
    const found = availableCurrencies.find(c => c.code === selectedCurrencyCode);
    if (found) return found;
    const base = availableCurrencies.find(c => Boolean(c.is_base)) || availableCurrencies[0];
    return base;
  }, [availableCurrencies, selectedCurrencyCode]);

  const activeExchangeRate = customRates[activeCurrency.code] !== undefined 
    ? customRates[activeCurrency.code] 
    : Number(activeCurrency.exchange_rate || 1.0);

  // Compute denomination totals across all currencies
  const denominationCalculations = useMemo(() => {
    let grandBaseTotal = 0;
    const perCurrency = {};

    availableCurrencies.forEach(curr => {
      const counts = denominationCounts[curr.code] || {};
      const rate = customRates[curr.code] !== undefined ? customRates[curr.code] : Number(curr.exchange_rate || 1.0);
      let currTotal = 0;
      (curr.denominations || []).forEach(d => {
        const count = Number(counts[d] || 0);
        currTotal += Number(d) * count;
      });
      const baseTotal = currTotal * rate;
      perCurrency[curr.code] = {
        currTotal,
        baseTotal,
        rate,
        counts
      };
      grandBaseTotal += baseTotal;
    });

    return { grandBaseTotal, perCurrency };
  }, [availableCurrencies, denominationCounts, customRates]);

  const handleUpdateDenomCount = (currCode, denom, count) => {
    const val = count === '' ? '' : Math.max(0, parseInt(count, 10) || 0);
    setDenominationCounts(prev => ({
      ...prev,
      [currCode]: {
        ...(prev[currCode] || {}),
        [denom]: val
      }
    }));
  };

  const handleIncrementDenomCount = (currCode, denom, step) => {
    const current = Number(denominationCounts[currCode]?.[denom] || 0);
    handleUpdateDenomCount(currCode, denom, Math.max(0, current + step));
  };

  const handleClearDenominations = (currCode) => {
    if (currCode) {
      setDenominationCounts(prev => ({ ...prev, [currCode]: {} }));
    } else {
      setDenominationCounts({});
    }
  };

  const openDenomDrawerFor = (target) => {
    setDenomTarget(target);
    const base = availableCurrencies.find(c => Boolean(c.is_base)) || availableCurrencies[0];
    setSelectedCurrencyCode(base.code);
    setIsDenomDrawerOpen(true);
  };

  const handleApplyDenominations = () => {
    const total = denominationCalculations.grandBaseTotal;
    if (denomTarget === 'start_shift') {
      setShiftStartFloat(total.toFixed(2));
      toast.success(`Opening Float set to ${currency} ${total.toFixed(2)} from counted denominations`);
    } else if (denomTarget === 'close_shift') {
      setShiftCloseCash(total.toFixed(2));
      toast.success(`Closing Cash set to ${currency} ${total.toFixed(2)} from counted denominations`);
    } else if (denomTarget === 'opening_balance') {
      setNewOpeningBalance(total.toFixed(2));
      toast.success(`Day Book Opening Balance set to ${currency} ${total.toFixed(2)}`);
    } else {
      toast.success(`Counted Total: ${currency} ${total.toFixed(2)}`);
    }
    setIsDenomDrawerOpen(false);
  };

  // Synchronize denomination count into float / closing cash whenever on denominations tab
  useEffect(() => {
    if (isStartShiftModalOpen && startShiftTab === 'denominations') {
      setShiftStartFloat(denominationCalculations.grandBaseTotal.toFixed(2));
    }
  }, [denominationCalculations.grandBaseTotal, isStartShiftModalOpen, startShiftTab]);

  useEffect(() => {
    if (isCloseShiftModalOpen && closeShiftTab === 'denominations') {
      setShiftCloseCash(denominationCalculations.grandBaseTotal.toFixed(2));
    }
  }, [denominationCalculations.grandBaseTotal, isCloseShiftModalOpen, closeShiftTab]);

  useEffect(() => {
    if (isOpeningModalOpen && openingModalTab === 'denominations') {
      setNewOpeningBalance(denominationCalculations.grandBaseTotal.toFixed(2));
    }
  }, [denominationCalculations.grandBaseTotal, isOpeningModalOpen, openingModalTab]);

  // Quick period changer for Day Book
  const handlePeriodPreset = (type) => {
    setFilterType(type);
    const now = new Date();
    if (type === 'today') {
      const d = formatDateStr(now);
      setFromDate(d);
      setToDate(d);
    } else if (type === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const d = formatDateStr(y);
      setFromDate(d);
      setToDate(d);
    } else if (type === 'custom') {
      setToDate(fromDate);
    }
  };

  const handleShiftPeriod = (days) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    from.setDate(from.getDate() + days);
    to.setDate(to.getDate() + days);
    setFromDate(formatDateStr(from));
    setToDate(formatDateStr(to));
  };

  function formatDateStr(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDisplayDate(dateStr) {
    if (!dateStr) return '';
    try {
      const [y, m, d] = dateStr.split('-');
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  function parseSafeDate(dateVal) {
    if (!dateVal) return null;
    if (dateVal instanceof Date) return dateVal;
    if (typeof dateVal === 'string') {
      const clean = dateVal.replace(' ', 'T').replace(/Z$/, '').replace(/\.\d+$/, '');
      const d = new Date(clean);
      if (!isNaN(d.getTime())) return d;
      const fallback = new Date(dateVal);
      if (!isNaN(fallback.getTime())) return fallback;
    }
    return null;
  }

  function formatDisplayTime(dateVal, options = { hour: '2-digit', minute: '2-digit' }) {
    const d = parseSafeDate(dateVal);
    if (!d) return '-';
    return d.toLocaleTimeString([], options);
  }

  function formatDisplayDateTime(dateVal, options = { dateStyle: 'short', timeStyle: 'short' }) {
    const d = parseSafeDate(dateVal);
    if (!d) return '-';
    return d.toLocaleString([], options);
  }

  // Calculate financial fields
  const invSummary = data?.invoiceSummary || {};
  const grossSales = Number(invSummary.total_gross || 0);
  const discount = Number(invSummary.total_discount || 0);
  const tax = Number(invSummary.total_tax || 0);
  const roundOff = Number(invSummary.total_round_off || 0);
  const complementary = Number(invSummary.total_complementary || 0);
  const netSales = Number(invSummary.net_sales || 0);
  const totalBilled = grossSales - discount + tax + roundOff;

  // Calculate sales due & due invoice counts for selected period
  const calculatedSalesDue = useMemo(() => {
    const invoices = data?.invoicesList || [];
    return invoices.reduce((acc, i) => {
      const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
      const status = (i.payment_status || '').toLowerCase();
      if (dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial') {
        const amt = Number(i.due_amount || 0) > 0 ? Number(i.due_amount) : Math.max(0, Number(i.total || 0) - Number(i.paid_amount || 0));
        return acc + amt;
      }
      return acc;
    }, 0);
  }, [data?.invoicesList]);

  const salesDue = Math.max(Number(data?.total_sales_due || 0), calculatedSalesDue);

  const dueInvoicesCount = useMemo(() => {
    const invoices = data?.invoicesList || [];
    return invoices.filter(i => {
      const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
      const status = (i.payment_status || '').toLowerCase();
      return dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial';
    }).length;
  }, [data?.invoicesList]);

  // Room revenue & room stay charges for selected period
  const totalRoomRevenue = Number(data?.total_room_revenue || 0);
  const roomChargesList = data?.room_charges_list || [];
  const roomChargesCount = roomChargesList.length;

  // Collections breakdown
  const salesPayments = data?.sales_payments || [];
  let calculatedCashCollected = 0;
  let calculatedQrCollected = 0;
  let calculatedCardCollected = 0;
  let calculatedOtherCollected = 0;
  let totalCollected = 0;

  salesPayments.forEach(p => {
    const amt = Number(p.total_amount || 0);
    totalCollected += amt;
    const m = (p.payment_method || '').toLowerCase();
    if (m.includes('cash')) calculatedCashCollected += amt;
    else if (m.includes('qr') || m.includes('fonepay') || m.includes('esewa') || m.includes('khalti') || m.includes('online') || m.includes('upi')) calculatedQrCollected += amt;
    else if (m.includes('card') || m.includes('pos') || m.includes('visa') || m.includes('master')) calculatedCardCollected += amt;
    else calculatedOtherCollected += amt;
  });

  const totalCashCollected = data?.total_cash_collected !== undefined ? Number(data.total_cash_collected) : calculatedCashCollected;
  const totalQrCollected = data?.total_qr_collected !== undefined ? Number(data.total_qr_collected) : calculatedQrCollected;
  const totalCardCollected = data?.total_card_collected !== undefined ? Number(data.total_card_collected) : calculatedCardCollected;
  if (totalCollected === 0 && (totalCashCollected > 0 || totalQrCollected > 0)) {
    totalCollected = totalCashCollected + totalQrCollected + totalCardCollected + Number(data?.total_other_collected || 0);
  }

  // Cash Flow breakdown
  const openingBalance = Number(data?.opening_balance || 0);
  const cashSales = totalCashCollected;
  const previousDueCollected = Number(data?.due_collected || 0);
  const otherCashReceipts = Number(data?.total_transfers_in || 0) + Number(data?.total_journal_in || 0);
  const totalCashIn = cashSales + previousDueCollected + otherCashReceipts;

  let cashPurchases = 0;
  let cashExpenses = 0;
  salesPayments.forEach(p => {
    const m = (p.payment_method || '').toLowerCase();
    if (m.includes('cash')) {
      cashPurchases += Number(p.purchases_paid || 0);
      cashExpenses += Number(p.expenses_paid || 0);
    }
  });
  if (cashExpenses === 0 && Number(data?.total_expenses || 0) > 0) {
    cashExpenses = Number(data?.total_expenses || 0);
  }
  const cashRefunds = 0;
  const cashWithdrawals = Number(data?.total_transfers_out || 0);
  const otherCashPayments = Number(data?.total_journal_out || 0);
  const totalCashOut = cashPurchases + cashExpenses + cashRefunds + cashWithdrawals + otherCashPayments;

  const expectedClosingCash = openingBalance + totalCashIn - totalCashOut;

  // Navigate to transactions tab
  const navigateToTransactions = (title, type) => {
    setTxFilter(type || 'all');
    setTxFilterTitle(title || 'Transactions');
    setTxSearch('');
    setActiveTab('transactions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Open drill-down modal & synchronize transactions tab
  const openDrillDown = (title, type) => {
    const filterKey = type || 'all';
    setTxFilter(filterKey);
    setTxFilterTitle(title || 'Transactions');
    setTxSearch('');
    setDrillSearch('');
    setDrillModal({
      isOpen: true,
      title: title || 'Filtered Transactions',
      type: filterKey
    });
  };

  // Filtered transactions for the Transaction Reports view
  const filteredTransactions = useMemo(() => {
    const invoices = data?.invoicesList || [];
    const compItems = data?.complementaryList || [];
    let list = [];

    if (txFilter === 'gross' || txFilter === 'billed' || txFilter === 'all') {
      list = invoices;
    } else if (txFilter === 'net') {
      list = invoices;
    } else if (txFilter === 'discount') {
      list = invoices.filter(i => Number(i.discount || 0) > 0);
    } else if (txFilter === 'tax') {
      list = invoices.filter(i => Number(i.tax_total || 0) > 0);
    } else if (txFilter === 'round_off') {
      list = invoices.filter(i => Number(i.round_off || 0) !== 0);
    } else if (txFilter === 'complementary') {
      list = compItems.map(c => ({
        id: c.order_id || c.id,
        invoice_no: `COMP-${c.token_no || c.id}`,
        token_no: c.token_no,
        created_at: c.date,
        customer_name: 'Complementary Order',
        payment_method_name: 'Complimentary',
        sub_total: c.total,
        total: c.total,
        discount: 0,
        tax_total: 0,
        round_off: 0,
        due_amount: 0,
        payment_status: 'complimentary',
        item_name: c.item_name,
        price: c.price,
        quantity: c.quantity,
        is_complementary: true
      }));
    } else if (txFilter === 'due') {
      list = invoices.filter(i => {
        const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
        const status = (i.payment_status || '').toLowerCase();
        return dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial';
      }).map(i => {
        const calculatedDue = Number(i.due_amount || 0) > 0 ? Number(i.due_amount) : Math.max(0, Number(i.total || 0) - Number(i.paid_amount || 0));
        return {
          ...i,
          due_amount: calculatedDue
        };
      });
    } else if (txFilter === 'due_collected') {
      const dcList = data?.due_collected_list || [];
      list = dcList.map(dc => ({
        id: dc.id,
        invoice_no: dc.invoice_no || `REC-${dc.id}`,
        token_no: dc.invoice_no,
        created_at: dc.payment_date || dc.created_at,
        customer_name: dc.customer_name || 'Customer Due Collection',
        payment_method_name: dc.payment_method || 'Cash',
        sub_total: dc.amount,
        total: dc.amount,
        paid_amount: dc.amount,
        due_amount: 0,
        payment_status: 'collected',
        is_due_collection: true
      }));
    } else if (txFilter === 'room_revenue') {
      const rList = data?.room_charges_list || [];
      list = rList.map(r => ({
        id: r.id,
        invoice_no: r.invoice_no || `ROOM-${r.room_number || r.id}`,
        token_no: r.room_number,
        created_at: r.created_at || r.date,
        customer_name: r.customer_name || `Room ${r.room_number} Guest`,
        customer_phone: r.customer_phone || '',
        payment_method_name: r.payment_method_name || 'Room Charge',
        sub_total: r.sub_total || r.total,
        total: r.total,
        tax_total: 0,
        discount: 0,
        paid_amount: r.paid_amount || r.total,
        due_amount: r.due_amount || 0,
        payment_status: r.payment_status || 'Paid',
        item_name: r.item_name || `Room ${r.room_number} Stay (${r.nights || 1} Night${(r.nights || 1) === 1 ? '' : 's'})`,
        price: r.room_rate || r.total,
        quantity: r.nights || 1,
        is_room_charge: true
      }));
    } else if (txFilter.startsWith('payment_')) {
      const method = txFilter.replace('payment_', '').toLowerCase();
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes(method));
    } else if (txFilter === 'cash') {
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes('cash'));
    } else if (txFilter === 'card') {
      list = invoices.filter(i => (i.payment_method_name || '').toLowerCase().includes('card'));
    } else if (txFilter === 'online' || txFilter === 'qr') {
      list = invoices.filter(i => {
        const m = (i.payment_method_name || '').toLowerCase();
        return m.includes('qr') || m.includes('fonepay') || m.includes('online') || m.includes('esewa') || m.includes('khalti');
      });
    } else {
      list = invoices;
    }

    if (!txSearch) return list;
    const q = txSearch.toLowerCase();
    return list.filter(item => {
      return (
        (item.invoice_no && item.invoice_no.toLowerCase().includes(q)) ||
        (item.token_no && String(item.token_no).includes(q)) ||
        (item.customer_name && item.customer_name.toLowerCase().includes(q)) ||
        (item.payment_method_name && item.payment_method_name.toLowerCase().includes(q)) ||
        (item.item_name && item.item_name.toLowerCase().includes(q)) ||
        (item.id && String(item.id).includes(q))
      );
    });
  }, [data?.invoicesList, data?.complementaryList, data?.due_collected_list, data?.room_charges_list, txFilter, txSearch]);

  const filteredTransactionsTotal = useMemo(() => {
    return filteredTransactions.reduce((acc, item) => {
      if (txFilter === 'due') {
        return acc + Number(item.due_amount || 0);
      }
      if (txFilter === 'discount') {
        return acc + Number(item.discount || 0);
      }
      if (txFilter === 'tax') {
        return acc + Number(item.tax_total || 0);
      }
      if (txFilter === 'round_off') {
        return acc + Math.abs(Number(item.round_off || 0));
      }
      return acc + Number(item.total || (item.price * item.quantity) || item.amount || 0);
    }, 0);
  }, [filteredTransactions, txFilter]);

  // Modal drilldown items computed from drillModal.type and drillSearch
  const modalDrillItems = useMemo(() => {
    if (!drillModal.isOpen) return [];
    const invoices = data?.invoicesList || [];
    const compItems = data?.complementaryList || [];
    let list = [];
    const t = drillModal.type;

    if (t === 'gross' || t === 'billed' || t === 'all') {
      list = invoices;
    } else if (t === 'net') {
      list = invoices;
    } else if (t === 'discount') {
      list = invoices.filter(i => Number(i.discount || 0) > 0);
    } else if (t === 'tax') {
      list = invoices.filter(i => Number(i.tax_total || 0) > 0);
    } else if (t === 'round_off') {
      list = invoices.filter(i => Number(i.round_off || 0) !== 0);
    } else if (t === 'complementary') {
      list = compItems.map(c => ({
        id: c.order_id || c.id,
        invoice_no: `COMP-${c.token_no || c.id}`,
        token_no: c.token_no,
        created_at: c.date,
        customer_name: 'Complementary Order',
        payment_method_name: 'Complimentary',
        sub_total: c.total,
        total: c.total,
        discount: 0,
        tax_total: 0,
        round_off: 0,
        due_amount: 0,
        payment_status: 'complimentary',
        item_name: c.item_name,
        price: c.price,
        quantity: c.quantity,
        is_complementary: true
      }));
    } else if (t === 'due') {
      list = invoices.filter(i => {
        const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
        const status = (i.payment_status || '').toLowerCase();
        return dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial';
      }).map(i => {
        const calculatedDue = Number(i.due_amount || 0) > 0 ? Number(i.due_amount) : Math.max(0, Number(i.total || 0) - Number(i.paid_amount || 0));
        return {
          ...i,
          due_amount: calculatedDue
        };
      });
    } else if (t === 'due_collected') {
      const dcList = data?.due_collected_list || [];
      list = dcList.map(dc => ({
        id: dc.id,
        invoice_no: dc.invoice_no || `REC-${dc.id}`,
        token_no: dc.invoice_no,
        created_at: dc.payment_date || dc.created_at,
        customer_name: dc.customer_name || 'Customer Due Collection',
        payment_method_name: dc.payment_method || 'Cash',
        sub_total: dc.amount,
        total: dc.amount,
        paid_amount: dc.amount,
        due_amount: 0,
        payment_status: 'collected',
        is_due_collection: true
      }));
    } else if (t === 'room_revenue') {
      const rList = data?.room_charges_list || [];
      list = rList.map(r => ({
        id: r.id,
        invoice_no: r.invoice_no || `ROOM-${r.room_number || r.id}`,
        token_no: r.room_number,
        created_at: r.created_at || r.date,
        customer_name: r.customer_name || `Room ${r.room_number} Guest`,
        customer_phone: r.customer_phone || '',
        payment_method_name: r.payment_method_name || 'Room Charge',
        sub_total: r.sub_total || r.total,
        total: r.total,
        tax_total: 0,
        discount: 0,
        paid_amount: r.paid_amount || r.total,
        due_amount: r.due_amount || 0,
        payment_status: r.payment_status || 'Paid',
        item_name: r.item_name || `Room ${r.room_number} Stay (${r.nights || 1} Night${(r.nights || 1) === 1 ? '' : 's'})`,
        price: r.room_rate || r.total,
        quantity: r.nights || 1,
        is_room_charge: true
      }));
    } else if (t.startsWith('payment_')) {
      const method = t.replace('payment_', '').toLowerCase();
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes(method));
    } else if (t === 'cash') {
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes('cash'));
    } else if (t === 'card') {
      list = invoices.filter(i => (i.payment_method_name || '').toLowerCase().includes('card'));
    } else if (t === 'online' || t === 'qr') {
      list = invoices.filter(i => {
        const m = (i.payment_method_name || '').toLowerCase();
        return m.includes('qr') || m.includes('fonepay') || m.includes('online') || m.includes('esewa') || m.includes('khalti');
      });
    } else if (t === 'purchases' || t === 'cash_purchases') {
      const purchases = data?.purchases || [];
      list = (t === 'cash_purchases'
        ? purchases.filter(p => !p.payment_method || p.payment_method.toLowerCase().includes('cash'))
        : purchases
      ).map(p => ({
        id: p.id,
        invoice_no: p.purchase_invoice_no || `PUR-${p.id}`,
        created_at: p.purchase_date,
        customer_name: p.supplier_name || 'Supplier Purchase',
        payment_method_name: p.payment_method || 'Cash',
        sub_total: p.total_amount,
        total: p.total_amount,
        discount: 0,
        tax_total: 0,
        paid_amount: p.paid_amount,
        due_amount: p.due_amount,
        payment_status: p.payment_status || (Number(p.due_amount) > 0 ? 'due' : 'paid'),
        is_purchase: true
      }));
    } else if (t === 'expenses' || t === 'cash_expenses') {
      const expenses = data?.expenses || [];
      list = (t === 'cash_expenses'
        ? expenses.filter(e => !e.payment_method || e.payment_method.toLowerCase().includes('cash'))
        : expenses
      ).map(e => ({
        id: e.id,
        invoice_no: `EXP-${e.id}`,
        created_at: e.date,
        customer_name: e.supplier_name || e.category_name || 'Expense Voucher',
        payment_method_name: e.payment_method || 'Cash',
        sub_total: e.amount,
        total: e.amount,
        discount: 0,
        tax_total: 0,
        paid_amount: e.paid_amount || e.amount,
        due_amount: e.due_amount || 0,
        payment_status: e.payment_status || (Number(e.due_amount) > 0 ? 'due' : 'paid'),
        item_name: e.description || e.category_name,
        is_expense: true
      }));
    } else {
      list = invoices;
    }

    if (!drillSearch) return list;
    const q = drillSearch.toLowerCase();
    return list.filter(item => {
      return (
        (item.invoice_no && item.invoice_no.toLowerCase().includes(q)) ||
        (item.token_no && String(item.token_no).includes(q)) ||
        (item.customer_name && item.customer_name.toLowerCase().includes(q)) ||
        (item.payment_method_name && item.payment_method_name.toLowerCase().includes(q)) ||
        (item.item_name && item.item_name.toLowerCase().includes(q)) ||
        (item.id && String(item.id).includes(q))
      );
    });
  }, [drillModal.isOpen, drillModal.type, drillSearch, data?.invoicesList, data?.complementaryList, data?.purchases, data?.expenses, data?.due_collected_list, data?.room_charges_list]);

  const modalDrillTotal = useMemo(() => {
    return modalDrillItems.reduce((acc, item) => {
      if (drillModal.type === 'due') {
        return acc + Number(item.due_amount || 0);
      }
      if (drillModal.type === 'discount') {
        return acc + Number(item.discount || 0);
      }
      if (drillModal.type === 'tax') {
        return acc + Number(item.tax_total || 0);
      }
      if (drillModal.type === 'round_off') {
        return acc + Math.abs(Number(item.round_off || 0));
      }
      return acc + Number(item.total || (item.price * item.quantity) || item.amount || 0);
    }, 0);
  }, [modalDrillItems, drillModal.type]);

  // Open invoice detailed breakdown modal
  const handleViewInvoice = async (invoice) => {
    try {
      setIsLoadingInvoice(true);
      toast.loading("Loading transaction details...");
      const invoiceId = invoice.id || invoice.invoice_id || invoice.raw_id;
      let orderData = null;
      try {
        const res = await getInvoiceOrders(invoiceId);
        if (res && res.status === 200) {
          orderData = res.data;
        }
      } catch (err) {
        console.warn("Could not fetch detailed orders:", err);
      }
      toast.dismiss();

      setViewingInvoice({
        ...invoice,
        ...(orderData || {}),
        invoiceId: invoiceId,
        invoiceNo: invoice.invoice_no || orderData?.invoice_no || `INV-${invoiceId}`
      });
    } catch (err) {
      toast.dismiss();
      console.error("Error viewing invoice:", err);
      setViewingInvoice({
        ...invoice,
        orders: [],
        invoiceId: invoice.id,
        invoiceNo: invoice.invoice_no || `INV-${invoice.id}`
      });
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  // Print thermal receipt for single transaction
  const handlePrintTransaction = async (invoice) => {
    try {
      toast.loading("Preparing receipt...");
      const invoiceId = invoice.id || invoice.invoice_id || invoice.raw_id;
      let orderData = null;
      try {
        const res = await getInvoiceOrders(invoiceId);
        if (res && res.status === 200) {
          orderData = res.data;
        }
      } catch (err) {
        console.warn("Could not fetch detailed orders, using invoice record:", err);
      }
      toast.dismiss();

      const ordersArr = orderData?.orders || [];
      let cartItems = [];

      for (const o of ordersArr) {
        (o.items || []).forEach(i => {
          cartItems.push({
            ...i,
            title: i.item_title || i.title || "Item",
            addons_ids: i?.addons?.length > 0 ? i.addons.map(a => a.id) : [],
            variant: i.variant_id ? { id: i.variant_id, title: i.variant_title, price: i.variant_price } : null
          });
        });
      }

      if (cartItems.length === 0) {
        cartItems.push({
          title: invoice.item_name || invoice.invoice_no || `Invoice #${invoiceId}`,
          quantity: invoice.quantity || 1,
          price: Number(invoice.sub_total || invoice.total || 0)
        });
      }

      const billFormat = printSettings?.bill_format || localStorage.getItem("bill_format") || "retail_tax_invoice";
      const pageFormat = printSettings?.page_format || localStorage.getItem("page_format") || "58";

      const receiptDetails = {
        cartItems,
        customerType: "CUSTOMER",
        customer: {
          id: invoice.customer_id || "Walk-in",
          name: invoice.customer_name || "Walk-in Customer"
        },
        tableId: null,
        currency: currency,
        storeSettings: store,
        printSettings: printSettings || {},
        billFormat,
        pageFormat,
        itemsTotal: Number(orderData?.subtotal !== undefined ? orderData.subtotal : (invoice.sub_total || invoice.total || 0)),
        taxTotal: Number(orderData?.taxTotal !== undefined ? orderData.taxTotal : (invoice.tax_total || 0)),
        serviceChargeTotal: Number(orderData?.serviceChargeTotal || 0),
        payableTotal: Number(orderData?.total !== undefined ? orderData.total : (invoice.total || 0)),
        storedDiscount: Number(orderData?.discount !== undefined ? orderData.discount : (invoice.discount || 0)),
        roundOff: Number(orderData?.roundOff !== undefined ? orderData.roundOff : (invoice.round_off || 0)),
        paidAmount: Number(orderData?.paidAmount !== undefined ? orderData.paidAmount : (invoice.paid_amount !== undefined ? invoice.paid_amount : invoice.total)),
        dueAmount: Number(orderData?.dueAmount !== undefined ? orderData.dueAmount : (invoice.due_amount || 0)),
        tokenNo: invoice.token_no,
        orderId: String(invoiceId),
        created_at: invoice.created_at || invoice.date || new Date(),
        date: invoice.created_at || invoice.date || new Date(),
        invoiceId: invoiceId,
        invoice_id: invoiceId,
        billNo: invoice.invoice_no || `INV-${invoiceId}`,
        invoiceNo: invoice.invoice_no || `INV-${invoiceId}`,
        paymentMethod: invoice.payment_method_name || "Cash"
      };

      setDetailsForReceiptPrint(receiptDetails);
      await printReceipt(receiptDetails);
      toast.success("Receipt sent to printer!");
    } catch (err) {
      toast.dismiss();
      console.error("Print receipt error:", err);
      toast.error("Failed to print receipt");
    }
  };

  // Print Shift Z-Report / Shift Statement / Shift Open Report
  const handlePrintShift = (shiftItem, type = 'shift_close') => {
    openPrintReport(type, shiftItem);
  };

  // CSV Export
  const handleExportCSV = () => {
    let csv = `Day Book Financial Report - ${storeName}\n`;
    csv += `Period: ${fromDate} to ${toDate}\n\n`;

    csv += `SALES SUMMARY\n`;
    csv += `Item,Amount (${currency})\n`;
    csv += `Gross Sales,${grossSales.toFixed(2)}\n`;
    csv += `Discount,-${discount.toFixed(2)}\n`;
    csv += `Net Sales,${netSales.toFixed(2)}\n`;
    csv += `VAT / Tax,${tax.toFixed(2)}\n`;
    csv += `Round Off,${roundOff.toFixed(2)}\n`;
    csv += `Complementary,${complementary.toFixed(2)}\n`;
    csv += `Total Billed,${totalBilled.toFixed(2)}\n\n`;

    csv += `COLLECTION SUMMARY\n`;
    csv += `Payment Type,Amount (${currency})\n`;
    salesPayments.forEach(p => {
      csv += `${p.payment_method},${Number(p.total_amount || 0).toFixed(2)}\n`;
    });
    csv += `Total Collected,${totalCollected.toFixed(2)}\n`;
    csv += `Sales Due,${salesDue.toFixed(2)}\n\n`;

    csv += `CASH FLOW\n`;
    csv += `Cash Opening,${openingBalance.toFixed(2)}\n`;
    csv += `Cash Sales,${cashSales.toFixed(2)}\n`;
    csv += `Previous Due Collection,${previousDueCollected.toFixed(2)}\n`;
    csv += `Other Cash Receipts,${otherCashReceipts.toFixed(2)}\n`;
    csv += `Cash Purchases,-${cashPurchases.toFixed(2)}\n`;
    csv += `Cash Expenses,-${cashExpenses.toFixed(2)}\n`;
    csv += `Expected Closing Cash,${expectedClosingCash.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DayBook_${fromDate}_to_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Save Opening Balance
  const handleSaveOpeningBalance = async () => {
    try {
      setIsSavingOB(true);
      await apiClient.post('/reports/day-book/opening-balance', {
        date: fromDate,
        amount: parseFloat(newOpeningBalance) || 0,
        denominations: denominationCounts
      });
      toast.success("Opening balance updated successfully!");
      setIsOpeningModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err.message || "Failed to update opening balance");
    } finally {
      setIsSavingOB(false);
    }
  };

  // Start Shift Handler
  const handleStartShiftSubmit = async (e) => {
    if (e) e.preventDefault();
    const floatAmount = parseFloat(shiftStartFloat) || 0;
    if (floatAmount <= 0 && !window.confirm("Start shift with 0.00 opening float?")) {
      return;
    }
    try {
      setIsStartingShift(true);
      await openShift(floatAmount, denominationCounts, shiftStartNote);

      toast.success("Shift started successfully!");
      setIsStartShiftModalOpen(false);
      setShiftStartFloat('');
      setShiftStartNote('');
      mutateShift();
      mutate();
      mutateClosedShifts();
    } catch (err) {
      console.error("Start shift error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to start shift");
    } finally {
      setIsStartingShift(false);
    }
  };

  // Close Shift Calculations
  const activeShiftOpening = Number(activeShift?.opening_balance || activeShift?.opening_cash || activeShift?.starting_float || 0);
  const activeShiftCashSales = Number(activeShift?.system_cash_sales !== undefined ? activeShift.system_cash_sales : (shiftSummary?.payments?.find(p => (p.payment_method || '').toLowerCase() === 'cash')?.total || 0));
  const activeShiftCashExpenses = Number(activeShift?.system_expenses !== undefined ? activeShift.system_expenses : (shiftSummary?.expenses?.find(e => (e.payment_method || '').toLowerCase() === 'cash')?.total || 0));
  const activeShiftExpectedCash = activeShift?.system_expected_cash !== undefined 
    ? Number(activeShift.system_expected_cash) 
    : (activeShiftOpening + activeShiftCashSales - activeShiftCashExpenses);
  const parsedCloseCash = parseFloat(shiftCloseCash) || 0;
  const activeShiftDiff = parsedCloseCash - activeShiftExpectedCash;

  // Close Shift Handler
  const handleCloseShiftSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!activeShift?.id) {
      toast.error("No active shift found to close");
      return;
    }
    try {
      setIsClosingShift(true);
      const actual = parseFloat(shiftCloseCash) || 0;
      await closeShift(activeShift.id, actual, denominationCounts, activeShiftExpectedCash, activeShiftDiff, shiftCloseNote);

      toast.success("Shift closed and reconciled successfully!");
      setIsCloseShiftModalOpen(false);
      setShiftCloseCash('');
      setShiftCloseNote('');
      mutateShift();
      mutate();
      mutateClosedShifts();
    } catch (err) {
      console.error("Close shift error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to close shift");
    } finally {
      setIsClosingShift(false);
    }
  };

  return (
    <Page>
      <div className="container-xl">
        {/* TOP NAVIGATION TABS */}
        <div className="d-print-none mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div className="d-flex align-items-center gap-2">
                <h2 className="page-title text-primary fw-bold mb-0">DAY BOOK & FINANCIAL CONTROL</h2>
                <span className="badge bg-blue-lt">Audit & Cash Reconciliation</span>
              </div>
              <div className="text-muted small">Manage Shifts, Business Registers, Denomination Calculator & Audit Records</div>
            </div>
            <div className="btn-group shadow-sm">
              <button 
                className={`btn btn-sm ${activeTab === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setActiveTab('daily'); handlePeriodPreset('today'); }}
              >
                <IconCalendar size={15} className="me-1" /> Daily Summary
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'range' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setActiveTab('range'); setFilterType('range'); }}
              >
                <IconFilter size={15} className="me-1" /> Date Range
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'shifts' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('shifts')}
              >
                <IconClock size={15} className="me-1" /> Shift Control
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'registers' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('registers')}
              >
                <IconBuildingStore size={15} className="me-1" /> Register Control
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'transactions' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('transactions')}
              >
                <IconReceipt size={15} className="me-1" /> Transaction Reports
              </button>
            </div>
          </div>
        </div>

        {/* FINANCIAL CONTROL STATUS RIBBON (Available across all views) */}
        <div className="card shadow-sm mb-3 d-print-none border-0 bg-white">
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              {/* Shift status */}
              <div className="d-flex align-items-center gap-3">
                {activeShift ? (
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-success-lt px-2 py-1 fw-bold fs-5">
                      <span className="status-dot status-dot-animated bg-success me-1"></span>
                      ACTIVE SHIFT #{activeShift.id}
                    </span>
                    <div className="small">
                      <span className="text-muted">Cashier:</span> <strong>{activeShift.opened_by_name || activeShift.opened_by || activeShift.cashier_name || 'Cashier'}</strong>
                      <span className="mx-2 text-muted">|</span>
                      <span className="text-muted">Opened:</span> <strong>{formatDisplayTime(activeShift.opened_at)}</strong>
                      <span className="mx-2 text-muted">|</span>
                      <span className="text-muted">Float:</span> <strong className="text-primary">{currency} {Number(activeShift.opening_balance || activeShift.opening_cash || 0).toFixed(2)}</strong>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-warning-lt px-2 py-1 fw-bold fs-5">
                      <span className="status-dot bg-warning me-1"></span>
                      NO ACTIVE SHIFT
                    </span>
                    <span className="small text-muted">Start shift to record physical drawer float and track cashier sales.</span>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {activeShift ? (
                  <div className="d-flex align-items-center gap-2">
                    <button 
                      type="button"
                      className="btn btn-sm btn-danger shadow-xs fw-bold"
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                      onClick={() => {
                        setShiftCloseCash(activeShiftExpectedCash.toFixed(2));
                        setIsCloseShiftModalOpen(true);
                      }}
                    >
                      <IconPlayerStop size={15} className="me-1" /> Close Shift
                    </button>
                    <button 
                      type="button"
                      className="btn btn-sm btn-outline-success shadow-xs fw-bold"
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                      onClick={() => {
                        if (window.confirm(`Active Shift #${activeShift.id || activeShift.shift_number} is currently in progress.\n\nTo start a new shift, the current shift must be closed first.\n\nWould you like to close Shift #${activeShift.id || activeShift.shift_number} now?`)) {
                          setShiftCloseCash(activeShiftExpectedCash.toFixed(2));
                          setIsCloseShiftModalOpen(true);
                        }
                      }}
                      title="Close active shift and start a new cashier session"
                    >
                      <IconPlayerPlay size={15} className="me-1" /> Start New Shift
                    </button>
                  </div>
                ) : (
                  <button 
                    type="button"
                    className="btn btn-sm btn-success shadow-xs fw-bold"
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    onClick={() => {
                      setShiftStartFloat('');
                      setShiftStartNote('');
                      setIsStartShiftModalOpen(true);
                    }}
                  >
                    <IconPlayerPlay size={15} className="me-1" /> Start Shift
                  </button>
                )}

                <button 
                  className="btn btn-sm btn-outline-primary shadow-xs"
                  onClick={() => openDenomDrawerFor('general')}
                  title="Open Multi-Currency Cash Denomination Calculator & Currency Converter"
                >
                  <IconCalculator size={15} className="me-1" /> Denomination Calculator
                </button>

                <button 
                  className="btn btn-sm btn-outline-secondary shadow-xs"
                  onClick={() => {
                    setNewOpeningBalance(String(openingBalance));
                    setIsOpeningModalOpen(true);
                  }}
                  title="Set Opening Cash Float for Day Book"
                >
                  <IconCoins size={15} className="me-1" /> Set Drawer Float
                </button>

                {/* Professional Printable Reports Menu */}
                <div className="position-relative" ref={printDropdownRef}>
                  <button 
                    type="button"
                    className="btn btn-sm text-white shadow-xs fw-bold d-flex align-items-center"
                    style={{ backgroundColor: '#4f46e5', borderColor: '#4f46e5', cursor: 'pointer', pointerEvents: 'auto' }}
                    onClick={() => setIsPrintDropdownOpen(!isPrintDropdownOpen)}
                    title="Generate and print professional reports with cashier profile verification"
                  >
                    <IconPrinter size={15} className="me-1" /> Print Reports <IconChevronRight size={13} className={`ms-1 transition-transform ${isPrintDropdownOpen ? 'rotate-90' : ''}`} />
                  </button>

                  {isPrintDropdownOpen && (
                    <div 
                      className="dropdown-menu dropdown-menu-end shadow-lg show p-1 border-0 rounded-3"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        zIndex: 1060,
                        minWidth: '270px',
                        display: 'block',
                        marginTop: '4px',
                        backgroundColor: '#ffffff'
                      }}
                    >
                      <div className="dropdown-header text-uppercase text-muted fw-bold py-1 px-3" style={{ fontSize: '10px' }}>
                        Shift Audit Reports
                      </div>
                      <button 
                        type="button" 
                        className="dropdown-item py-1.5 px-3 rounded-2 d-flex align-items-center"
                        onClick={() => openPrintReport('shift_open', activeShift)}
                      >
                        <span className="badge bg-success-lt me-2 p-1"><IconReceipt size={14} /></span>
                        <div>
                          <div className="fw-bold text-dark">Shift Open Report</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>Float & Multi-Currency Denominations</div>
                        </div>
                      </button>
                      <button 
                        type="button" 
                        className="dropdown-item py-1.5 px-3 rounded-2 d-flex align-items-center"
                        onClick={() => openPrintReport('shift_close', activeShift)}
                      >
                        <span className="badge bg-danger-lt me-2 p-1"><IconReceipt size={14} /></span>
                        <div>
                          <div className="fw-bold text-dark">Shift Close Z-Report</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>Collections, Due, Expenses, Purchases</div>
                        </div>
                      </button>

                      <div className="dropdown-divider my-1"></div>
                      <div className="dropdown-header text-uppercase text-muted fw-bold py-1 px-3" style={{ fontSize: '10px' }}>
                        Financial & Audit Reports
                      </div>
                      <button 
                        type="button" 
                        className="dropdown-item py-1.5 px-3 rounded-2 d-flex align-items-center"
                        onClick={() => openPrintReport('sales')}
                      >
                        <span className="badge bg-primary-lt me-2 p-1"><IconFileText size={14} /></span>
                        <div>
                          <div className="fw-bold text-dark">Sales Audit Report</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>Gross, Net, Tax, Bill Invoices</div>
                        </div>
                      </button>
                      <button 
                        type="button" 
                        className="dropdown-item py-1.5 px-3 rounded-2 d-flex align-items-center"
                        onClick={() => openPrintReport('purchase')}
                      >
                        <span className="badge bg-info-lt me-2 p-1"><IconBuildingStore size={14} /></span>
                        <div>
                          <div className="fw-bold text-dark">Purchase Audit Report</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>Supplier Orders, Dues & Payments</div>
                        </div>
                      </button>
                      <button 
                        type="button" 
                        className="dropdown-item py-1.5 px-3 rounded-2 d-flex align-items-center"
                        onClick={() => openPrintReport('expenses')}
                      >
                        <span className="badge bg-warning-lt me-2 p-1"><IconCash size={14} /></span>
                        <div>
                          <div className="fw-bold text-dark">Expenses Audit Report</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>Cash & Bank Expenditure Log</div>
                        </div>
                      </button>
                      <button 
                        type="button" 
                        className="dropdown-item py-1.5 px-3 rounded-2 d-flex align-items-center"
                        onClick={() => openPrintReport('register')}
                      >
                        <span className="badge bg-secondary-lt me-2 p-1"><IconBuildingStore size={14} /></span>
                        <div>
                          <div className="fw-bold text-dark">Register Audit Report</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>Daily Master Drawer Audit</div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                <button 
                  className="btn btn-sm btn-ghost-secondary"
                  onClick={() => {
                    mutate();
                    mutateShift();
                    mutateClosedShifts();
                    mutateCurrentReg();
                    toast.success("Data refreshed!");
                  }}
                  title="Refresh All Records"
                >
                  <IconRefresh size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* TAB 1 & 2: DAY BOOK SUMMARY & DATE RANGE */}
        {(activeTab === 'daily' || activeTab === 'range') && (
          <>
            {/* DATE FILTER HEADER */}
            <div className="card shadow-sm mb-3 d-print-none border-0">
              <div className="card-body p-3">
                <div className="row g-2 align-items-center">
                  <div className="col-auto">
                    <span className="fw-bold me-2 small text-muted text-uppercase">Report Period:</span>
                    <div className="btn-group btn-group-sm">
                      <button 
                        className={`btn ${filterType === 'today' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => handlePeriodPreset('today')}
                      >
                        Today
                      </button>
                      <button 
                        className={`btn ${filterType === 'yesterday' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => handlePeriodPreset('yesterday')}
                      >
                        Yesterday
                      </button>
                      <button 
                        className={`btn ${filterType === 'custom' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => handlePeriodPreset('custom')}
                      >
                        Custom Date
                      </button>
                      <button 
                        className={`btn ${filterType === 'range' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setFilterType('range')}
                      >
                        Date Range
                      </button>
                    </div>
                  </div>

                  <div className="col-auto d-flex align-items-center gap-2">
                    <span className="small text-muted">From:</span>
                    <input 
                      type="date" 
                      className="form-control form-control-sm" 
                      style={{ width: '140px' }}
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        if (filterType !== 'range') setToDate(e.target.value);
                      }}
                    />
                    {filterType === 'range' && (
                      <>
                        <span className="small text-muted">To:</span>
                        <input 
                          type="date" 
                          className="form-control form-control-sm" 
                          style={{ width: '140px' }}
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </>
                    )}
                  </div>

                  {filterType !== 'range' && (
                    <div className="col-auto">
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" onClick={() => handleShiftPeriod(-1)} title="Previous Day">
                          <IconChevronLeft size={16} />
                        </button>
                        <button className="btn btn-outline-secondary" onClick={() => handleShiftPeriod(1)} title="Next Day">
                          <IconChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="col ms-auto text-end d-flex justify-content-end gap-2 flex-wrap">
                    <button className="btn btn-sm btn-outline-secondary" onClick={handleExportCSV}>
                      <IconFileSpreadsheet size={16} className="me-1" /> Export CSV
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-warning" 
                      onClick={() => openPrintReport('expenses')}
                      title="Print Expenses Audit Report"
                    >
                      <IconCash size={15} className="me-1" /> Print Expenses
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-info" 
                      onClick={() => openPrintReport('purchase')}
                      title="Print Purchase Audit Report"
                    >
                      <IconBuildingStore size={15} className="me-1" /> Print Purchases
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-primary fw-bold" 
                      onClick={() => openPrintReport('sales')}
                      title="Print Official Sales Audit Report"
                    >
                      <IconPrinter size={16} className="me-1" /> Print Sales Report
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* MAIN REPORT CARD */}
            <div className="card shadow-sm border-0 mb-4 bg-white">
              <div className="card-body p-4">
                {/* Store Printable Header */}
                <div className="text-center mb-4 pb-3 border-bottom">
                  <h2 className="fw-bold text-dark m-0">{storeName}</h2>
                  <div className="text-muted small">{storeAddress}</div>
                  <div className="text-muted small">PAN/VAT No: {storePan} | Phone: {storePhone}</div>
                  <div className="mt-2">
                    <span className="badge bg-primary-lt fs-5 px-3 py-1 fw-bold text-uppercase">
                      DAY BOOK FINANCIAL REPORT
                    </span>
                  </div>
                  <div className="small text-secondary mt-1">
                    Audit Period: <strong>{formatDisplayDate(fromDate)}</strong> {fromDate !== toDate ? ` to ${formatDisplayDate(toDate)}` : ''}
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <div className="mt-2 text-muted">Auditing financial transactions...</div>
                  </div>
                ) : isError ? (
                  <div className="alert alert-danger text-center">
                    Failed to load day book report. Please verify connection and retry.
                  </div>
                ) : (
                  <>
                    {/* EXECUTIVE KPI SUMMARY CARDS */}
                    <div className="row g-3 mb-4">
                      {/* CARD 1: TOTAL BILLED SALES */}
                      <div className="col-12 col-sm-6 col-lg-4 col-xl">
                        <div className="card card-sm border shadow-xs h-100 bg-white">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted small fw-bold text-uppercase tracking-wider">Billed Sales</span>
                              <div className="avatar avatar-xs bg-primary-lt rounded-circle">
                                <IconReceipt size={18} className="text-primary" />
                              </div>
                            </div>
                            <div className="d-flex align-items-baseline justify-content-between">
                              <div className="fs-2 fw-bold text-dark">
                                {currency} {totalBilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <button 
                                type="button"
                                className="btn btn-sm btn-icon btn-ghost-primary"
                                title="View All Billed Invoices (👁)"
                                onClick={() => openDrillDown('All Billed Invoices', 'billed')}
                              >
                                <IconEye size={18} />
                              </button>
                            </div>
                            <div className="text-muted small mt-1 d-flex justify-content-between align-items-center">
                              <span>Invoiced Gross: {currency} {grossSales.toFixed(2)}</span>
                              <span className="badge bg-blue-lt">Audited</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CARD 2: TOTAL COLLECTIONS */}
                      <div className="col-12 col-sm-6 col-lg-4 col-xl">
                        <div className="card card-sm border shadow-xs h-100 bg-white">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted small fw-bold text-uppercase tracking-wider">Collections</span>
                              <div className="avatar avatar-xs bg-green-lt rounded-circle">
                                <IconCoins size={18} className="text-success" />
                              </div>
                            </div>
                            <div className="d-flex align-items-baseline justify-content-between">
                              <div className="fs-2 fw-bold text-success">
                                {currency} {totalCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <button 
                                type="button"
                                className="btn btn-sm btn-icon btn-ghost-success"
                                title="View All Collections (👁)"
                                onClick={() => openDrillDown('All Collected Invoices', 'all')}
                              >
                                <IconEye size={18} />
                              </button>
                            </div>
                            <div className="text-muted small mt-1 d-flex justify-content-between align-items-center">
                              <span>Cash: {currency} {totalCashCollected.toFixed(2)} | QR: {currency} {totalQrCollected.toFixed(2)}</span>
                              <span className="badge bg-green-lt">Received</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CARD 3: SALES DUE (CREDIT CREATED) WITH PROMINENT EYE ICON */}
                      <div className="col-12 col-sm-6 col-lg-4 col-xl">
                        <div 
                          className="card card-sm border border-warning shadow-xs h-100 bg-warning-lt"
                          style={{ borderLeft: '4px solid #f59f00' }}
                        >
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-warning-emphasis small fw-bold text-uppercase tracking-wider">Sales Due (Credit)</span>
                              <span className="badge bg-warning text-white fw-bold">
                                {dueInvoicesCount} {dueInvoicesCount === 1 ? 'Invoice' : 'Invoices'}
                              </span>
                            </div>
                            <div className="d-flex align-items-baseline justify-content-between">
                              <div className="fs-2 fw-bold text-warning">
                                {currency} {salesDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <button 
                                type="button"
                                className="btn btn-sm btn-warning text-white fw-bold d-flex align-items-center gap-1 shadow-xs"
                                title="Click to view all Due Transactions for selected date (👁)"
                                onClick={() => openDrillDown('Due / Credit Sales Invoices', 'due')}
                              >
                                <IconEye size={16} />
                                <span>View Due</span>
                              </button>
                            </div>
                            <div className="text-muted small mt-1 d-flex justify-content-between align-items-center">
                              <span className="text-secondary">Receivables on this date</span>
                              <span className="text-warning fw-semibold">Audit Drilldown</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CARD 4: ROOM CHARGES REVENUE */}
                      <div className="col-12 col-sm-6 col-lg-4 col-xl">
                        <div 
                          className="card card-sm border border-indigo shadow-xs h-100 bg-indigo-lt"
                          style={{ borderLeft: '4px solid #4263eb' }}
                        >
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-indigo-emphasis small fw-bold text-uppercase tracking-wider">Room Revenue</span>
                              <span className="badge bg-indigo text-white fw-bold">
                                {roomChargesCount} {roomChargesCount === 1 ? 'Stay' : 'Stays'}
                              </span>
                            </div>
                            <div className="d-flex align-items-baseline justify-content-between">
                              <div className="fs-2 fw-bold text-indigo">
                                {currency} {totalRoomRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <button 
                                type="button"
                                className="btn btn-sm btn-indigo text-white fw-bold d-flex align-items-center gap-1 shadow-xs"
                                title="Click to view all Room Stay charges for selected date (👁)"
                                onClick={() => openDrillDown('Room Charges Revenue', 'room_revenue')}
                              >
                                <IconEye size={16} />
                                <span>View Rooms</span>
                              </button>
                            </div>
                            <div className="text-muted small mt-1 d-flex justify-content-between align-items-center">
                              <span className="text-secondary">Accommodation Stay Charges</span>
                              <span className="text-indigo fw-semibold">Audit Drilldown</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CARD 5: EXPECTED DRAWER CASH */}
                      <div className="col-12 col-sm-6 col-lg-4 col-xl">
                        <div className="card card-sm border shadow-xs h-100 bg-white">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-muted small fw-bold text-uppercase tracking-wider">Drawer Cash</span>
                              <div className="avatar avatar-xs bg-azure-lt rounded-circle">
                                <IconCash size={18} className="text-azure" />
                              </div>
                            </div>
                            <div className="d-flex align-items-baseline justify-content-between">
                              <div className="fs-2 fw-bold text-dark">
                                {currency} {expectedClosingCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <button 
                                type="button"
                                className="btn btn-sm btn-icon btn-ghost-primary"
                                title="View Cash Collections (👁)"
                                onClick={() => openDrillDown('Cash Collections', 'cash')}
                              >
                                <IconEye size={18} />
                              </button>
                            </div>
                            <div className="text-muted small mt-1 d-flex justify-content-between align-items-center">
                              <span>Float: {currency} {openingBalance.toFixed(2)} | In: +{currency} {totalCashIn.toFixed(2)}</span>
                              <span className="badge bg-purple-lt">Drawer In-Hand</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 4: THREE-COLUMN FINANCIAL OVERVIEW */}
                    <div className="row g-3 mb-4">
                      {/* SALES SUMMARY */}
                      <div className="col-lg-6">
                        <div className="border rounded-3 p-3 h-100 bg-light-lt">
                          <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                            <h4 className="fw-bold text-uppercase text-secondary m-0">SALES SUMMARY</h4>
                            <span className="badge bg-blue-lt">Audited Figures</span>
                          </div>

                          <div className="table-responsive">
                            <table className="table table-vcenter table-hover table-bordered bg-white shadow-xs">
                              <thead className="table-light">
                                <tr>
                                  <th>Item</th>
                                  <th className="text-end">Amount</th>
                                  <th className="text-center" style={{ width: '90px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="fw-medium">Gross Sales</td>
                                  <td className="text-end fw-bold">
                                    {currency} {grossSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View Underlying Invoices"
                                      onClick={() => openDrillDown('Gross Sales Invoices', 'gross')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-medium text-danger">Discount</td>
                                  <td className="text-end text-danger fw-bold">
                                    -{currency} {discount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View Discounted Sales"
                                      onClick={() => openDrillDown('Discounted Invoices', 'discount')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                      title="Print Discount Report"
                                      onClick={() => window.print()}
                                    >
                                      <IconPrinter size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-bold text-dark">Net Sales</td>
                                  <td className="text-end fw-bold text-dark">
                                    {currency} {netSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View Net Sales Invoices"
                                      onClick={() => openDrillDown('Net Sales Invoices', 'net')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-medium">VAT / Tax</td>
                                  <td className="text-end fw-bold text-indigo">
                                    {currency} {tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View VAT Invoices"
                                      onClick={() => openDrillDown('Tax / VAT Invoices', 'tax')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                      title="Print VAT Invoices"
                                      onClick={() => window.print()}
                                    >
                                      <IconPrinter size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-medium">Round Off</td>
                                  <td className="text-end fw-medium text-muted">
                                    {roundOff < 0 ? '-' : ''}{currency} {Math.abs(roundOff).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View Round Off Logs"
                                      onClick={() => openDrillDown('Round Off Invoices', 'round_off')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                      title="Print"
                                      onClick={() => window.print()}
                                    >
                                      <IconPrinter size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-medium">Complementary Orders</td>
                                  <td className="text-end text-muted">
                                    {currency} {complementary.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View Complementary Invoices"
                                      onClick={() => openDrillDown('Complementary Orders', 'complementary')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr className="table-primary border-top border-2">
                                  <td className="fw-bold text-primary fs-4">Total Billed Sales</td>
                                  <td className="text-end fw-bold text-primary fs-4">
                                    {currency} {totalBilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-primary" 
                                      title="View All Invoices"
                                      onClick={() => openDrillDown('All Billed Invoices', 'billed')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr className="bg-warning-lt">
                                  <td className="fw-bold text-warning">
                                    <span className="badge bg-warning me-1 text-white">CREDIT</span>
                                    Sales Due (Receivables Created)
                                  </td>
                                  <td className="text-end fw-bold text-warning">
                                    {currency} {salesDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-warning" 
                                      title="View Sales Due Invoices"
                                      onClick={() => openDrillDown('Due / Credit Sales Invoices', 'due')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr className="bg-indigo-lt">
                                  <td className="fw-bold text-indigo">
                                    <span className="badge bg-indigo me-1 text-white">ROOMS</span>
                                    Room Charges (Stay Revenue)
                                  </td>
                                  <td className="text-end fw-bold text-indigo">
                                    {currency} {totalRoomRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-indigo" 
                                      title="View Room Stay Charges (👁)"
                                      onClick={() => openDrillDown('Room Charges Revenue', 'room_revenue')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      {/* COLLECTION SUMMARY */}
                      <div className="col-lg-6">
                        <div className="border rounded-3 p-3 h-100 bg-light-lt">
                          <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                            <h4 className="fw-bold text-uppercase text-secondary m-0">COLLECTION SUMMARY</h4>
                            <span className="badge bg-green-lt">Payment Reconciled</span>
                          </div>

                          {/* Quick KPI Highlights for Cash & QR */}
                          <div className="row g-2 mb-3">
                            <div className="col-6">
                              <div 
                                className="p-2 rounded-2 border bg-white d-flex align-items-center justify-content-between shadow-xs cursor-pointer"
                                style={{ borderLeft: '4px solid #70B56A' }}
                                onClick={() => openDrillDown('Cash Collections', 'cash')}
                                title="Click to view Cash transactions"
                              >
                                <div>
                                  <div className="text-muted small fw-semibold">💵 Total Cash</div>
                                  <div className="fw-bold fs-3 text-dark">
                                    {currency} {totalCashCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                </div>
                                <IconEye size={18} className="text-secondary" />
                              </div>
                            </div>
                            <div className="col-6">
                              <div 
                                className="p-2 rounded-2 border bg-white d-flex align-items-center justify-content-between shadow-xs cursor-pointer"
                                style={{ borderLeft: '4px solid #10b981' }}
                                onClick={() => openDrillDown('QR & Digital Collections', 'qr')}
                                title="Click to view QR / Digital transactions"
                              >
                                <div>
                                  <div className="text-muted small fw-semibold">📱 Total QR / Digital</div>
                                  <div className="fw-bold fs-3 text-success">
                                    {currency} {totalQrCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                </div>
                                <IconEye size={18} className="text-success" />
                              </div>
                            </div>
                          </div>

                          <div className="table-responsive">
                            <table className="table table-vcenter table-hover table-bordered bg-white shadow-xs">
                              <thead className="table-light">
                                <tr>
                                  <th>Payment Type</th>
                                  <th className="text-end">Amount</th>
                                  <th className="text-center" style={{ width: '90px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salesPayments.map((p, idx) => (
                                  <tr key={idx}>
                                    <td className="fw-medium">{p.payment_method}</td>
                                    <td className="text-end fw-bold">
                                      {currency} {Number(p.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="text-center">
                                      <button 
                                        className="btn btn-sm btn-icon btn-ghost-primary" 
                                        title={`View ${p.payment_method} Transactions`}
                                        onClick={() => openDrillDown(`${p.payment_method} Collections`, `payment_${p.payment_method}`)}
                                      >
                                        <IconEye size={17} />
                                      </button>
                                      <button 
                                        className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                        title={`Print ${p.payment_method} Summary`}
                                        onClick={() => window.print()}
                                      >
                                        <IconPrinter size={17} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                                {salesPayments.length === 0 && (
                                  <tr>
                                    <td colSpan={3} className="text-center text-muted py-3">No collections recorded in this period.</td>
                                  </tr>
                                )}

                                {/* Dedicated Subtotal Rows for Cash & QR */}
                                <tr className="border-top border-2 bg-light">
                                  <td className="fw-bold text-dark">💵 Total Cash Collected</td>
                                  <td className="text-end fw-bold text-dark">
                                    {currency} {totalCashCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View All Cash Collections"
                                      onClick={() => openDrillDown('Cash Collections', 'cash')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>

                                <tr className="bg-light">
                                  <td className="fw-bold text-success">📱 Total QR / Digital Collected</td>
                                  <td className="text-end fw-bold text-success">
                                    {currency} {totalQrCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-success" 
                                      title="View All QR / Digital Collections"
                                      onClick={() => openDrillDown('QR & Digital Collections', 'qr')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>

                                <tr className="table-success border-top border-2">
                                  <td className="fw-bold text-success fs-4">Total Collected Receipts</td>
                                  <td className="text-end fw-bold text-success fs-4">
                                    {currency} {totalCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-success" 
                                      title="View All Collections"
                                      onClick={() => openDrillDown('All Collected Invoices', 'all')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-bold text-warning">Sales Due (Receivables)</td>
                                  <td className="text-end fw-bold text-warning">
                                    {currency} {salesDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title="View Due Invoices"
                                      onClick={() => openDrillDown('Due / Credit Invoices', 'due')}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 5: PHYSICAL CASH FLOW RECONCILIATION */}
                    <div className="border rounded-3 p-3 bg-light-lt">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <div>
                          <h4 className="fw-bold text-uppercase text-secondary m-0">PHYSICAL CASH FLOW RECONCILIATION</h4>
                          <div className="small text-muted">Audited Drawer Inflow, Outflow & Expected Physical Cash in Hand</div>
                        </div>
                        <div className="d-flex gap-2">
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openDenomDrawerFor('opening_balance')}
                          >
                            <IconCalculator size={15} className="me-1" /> Count Cash Drawer
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              setNewOpeningBalance(String(openingBalance));
                              setIsOpeningModalOpen(true);
                            }}
                          >
                            <IconCoins size={15} className="me-1" /> Edit Float
                          </button>
                        </div>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="bg-white p-3 rounded-2 shadow-xs border">
                            <div className="fw-bold text-success mb-2 border-bottom pb-1">CASH INFLOWS (+)</div>
                            <table className="table table-sm table-borderless m-0">
                              <tbody>
                                <tr>
                                  <td className="text-muted">Opening Cash Float:</td>
                                  <td className="text-end fw-bold text-dark">
                                    {currency} {openingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="d-flex align-items-center justify-content-between">
                                    <span>• Cash Sales Collected</span>
                                    <button 
                                      type="button"
                                      className="btn btn-sm btn-icon btn-ghost-success py-0 px-1 ms-1"
                                      title="View Cash Sales Invoices"
                                      onClick={() => openDrillDown('Cash Sales Invoices', 'cash')}
                                    >
                                      <IconEye size={15} />
                                    </button>
                                  </td>
                                  <td className="text-end fw-medium text-success">
                                    +{currency} {cashSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="d-flex align-items-center justify-content-between">
                                    <span>• Previous Due Collected</span>
                                    <button 
                                      type="button"
                                      className="btn btn-sm btn-icon btn-ghost-success py-0 px-1 ms-1"
                                      title="View Previous Due Collections"
                                      onClick={() => openDrillDown('Previous Due Collections', 'due_collected')}
                                    >
                                      <IconEye size={15} />
                                    </button>
                                  </td>
                                  <td className="text-end fw-medium text-success">
                                    +{currency} {previousDueCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Other Cash Receipts / Transfers In</td>
                                  <td className="text-end fw-medium text-success">
                                    +{currency} {otherCashReceipts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr className="bg-success-lt fw-bold">
                                  <td>Total Cash Inflow (inc. Float)</td>
                                  <td className="text-end text-success">
                                    {currency} {(openingBalance + totalCashIn).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="bg-white p-3 rounded-2 shadow-xs border">
                            <div className="fw-bold text-danger mb-2 border-bottom pb-1">CASH OUTFLOWS (-)</div>
                            <table className="table table-sm table-borderless m-0">
                              <tbody>
                                <tr>
                                  <td className="d-flex align-items-center justify-content-between">
                                    <span>• Cash Purchases Paid</span>
                                    <button 
                                      type="button"
                                      className="btn btn-sm btn-icon btn-ghost-danger p-0 ms-1"
                                      title="View Cash Purchases Vouchers"
                                      style={{ width: '22px', height: '22px' }}
                                      onClick={() => openDrillDown('Cash Purchases Outflows', 'cash_purchases')}
                                    >
                                      <IconEye size={15} />
                                    </button>
                                  </td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashPurchases.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="d-flex align-items-center justify-content-between">
                                    <span>• Cash Expenses Paid</span>
                                    <button 
                                      type="button"
                                      className="btn btn-sm btn-icon btn-ghost-danger p-0 ms-1"
                                      title="View Cash Expenses Vouchers"
                                      style={{ width: '22px', height: '22px' }}
                                      onClick={() => openDrillDown('Cash Expenses Outflows', 'cash_expenses')}
                                    >
                                      <IconEye size={15} />
                                    </button>
                                  </td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Cash Refunds</td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashRefunds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Cash Withdrawals / Transfers Out</td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashWithdrawals.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr className="bg-danger-lt fw-bold">
                                  <td>Total Cash Outflow</td>
                                  <td className="text-end text-danger">
                                    -{currency} {totalCashOut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      {/* Expected Closing Cash Banner */}
                      <div className="card mt-3 bg-dark text-white border-0 shadow-sm">
                        <div className="card-body py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            <div className="text-uppercase small text-white-50 letter-spacing-1">
                              Formula: Opening Cash ({currency} {openingBalance.toFixed(2)}) + Cash In ({currency} {totalCashIn.toFixed(2)}) - Cash Out ({currency} {totalCashOut.toFixed(2)})
                            </div>
                            <div className="fs-2 fw-bold text-white">Expected Closing Cash</div>
                          </div>
                          <div className="fs-1 fw-bold text-success text-end">
                            {currency} {expectedClosingCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* TAB 3: SHIFT CONTROL */}
        {activeTab === 'shifts' && (
          <div className="mb-4">
            {/* Active Shift Card */}
            <div className="card shadow-sm border-0 mb-4 bg-white">
              <div className="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h3 className="card-title fw-bold text-dark m-0">
                    <IconClock size={18} className="me-2 text-primary" /> Active Cashier Shift
                  </h3>
                  <div className="small text-muted">Current cashier shift session and live drawer reconciliation</div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {activeShift ? (
                    <>
                      <button 
                        type="button"
                        className="btn btn-outline-info shadow-xs fw-bold"
                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        onClick={() => openPrintReport('shift_open', activeShift)}
                        title="Print Shift Open Report (Float & Denominations)"
                      >
                        <IconPrinter size={16} className="me-1" /> Shift Open Report
                      </button>
                      <button 
                        type="button"
                        className="btn btn-outline-primary shadow-xs fw-bold"
                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        onClick={() => openPrintReport('shift_close', activeShift)}
                        title="Print Live Shift Audit Report"
                      >
                        <IconPrinter size={16} className="me-1" /> Live Shift Report
                      </button>
                      <button 
                        type="button"
                        className="btn btn-danger shadow-xs fw-bold"
                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        onClick={() => {
                          setShiftCloseCash(activeShiftExpectedCash.toFixed(2));
                          setIsCloseShiftModalOpen(true);
                        }}
                      >
                        <IconPlayerStop size={16} className="me-1" /> End & Close Shift
                      </button>
                    </>
                  ) : (
                    <button 
                      type="button"
                      className="btn btn-success shadow-xs fw-bold"
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                      onClick={() => {
                        setShiftStartFloat('');
                        setShiftStartNote('');
                        setIsStartShiftModalOpen(true);
                      }}
                    >
                      <IconPlayerPlay size={16} className="me-1" /> Start New Shift
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body p-4">
                {isLoadingShift ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                    <div className="mt-2 text-muted">Loading active shift...</div>
                  </div>
                ) : activeShift ? (
                  <>
                    <div className="row g-3 mb-4">
                      <div className="col-sm-6 col-lg-3">
                        <div className="border rounded-3 p-3 bg-light">
                          <div className="text-muted small">Shift ID & Cashier</div>
                          <div className="fs-3 fw-bold text-dark">#{activeShift.id || activeShift.shift_number}</div>
                          <div className="small text-muted">{activeShift.opened_by_name || activeShift.opened_by || activeShift.cashier_name || 'Cashier'}</div>
                        </div>
                      </div>
                      <div className="col-sm-6 col-lg-3">
                        <div className="border rounded-3 p-3 bg-light">
                          <div className="text-muted small">Opening Drawer Float</div>
                          <div className="fs-3 fw-bold text-primary">{currency} {activeShiftOpening.toFixed(2)}</div>
                          <div className="small text-muted">Opened: {formatDisplayTime(activeShift.opened_at)}</div>
                        </div>
                      </div>
                      <div className="col-sm-6 col-lg-3">
                        <div className="border rounded-3 p-3 bg-light">
                          <div className="text-muted small">Cash Sales Collected</div>
                          <div className="fs-3 fw-bold text-success">+{currency} {activeShiftCashSales.toFixed(2)}</div>
                          <div className="small text-muted">Paid in Cash</div>
                        </div>
                      </div>
                      <div className="col-sm-6 col-lg-3">
                        <div className="border rounded-3 p-3 bg-light">
                          <div className="text-muted small">Expected Drawer Cash</div>
                          <div className="fs-3 fw-bold text-indigo">{currency} {activeShiftExpectedCash.toFixed(2)}</div>
                          <div className="small text-muted">Float + Cash Sales - Expenses</div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h4 className="fw-bold m-0 text-dark">Shift Transaction Activity</h4>
                      <span className="badge bg-primary-lt">{shiftSummary?.transactions?.length || 0} Transactions</span>
                    </div>

                    <div className="table-responsive border rounded-2">
                      <table className="table table-vcenter table-hover table-striped m-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Time</th>
                            <th>Ref #</th>
                            <th>Payment</th>
                            <th>Customer</th>
                            <th className="text-end">Amount</th>
                            <th className="text-center" style={{ width: '100px' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(shiftSummary?.transactions || []).map((tx, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td className="small text-muted">{formatDisplayTime(tx.time || tx.created_at)}</td>
                              <td className="fw-bold">{tx.ref_no || tx.invoice_no || `INV-${tx.id}`}</td>
                              <td><span className="badge bg-secondary-lt">{tx.payment_method}</span></td>
                              <td>{tx.customer_name || 'Walk-in'}</td>
                              <td className="text-end fw-bold">{currency} {Number(tx.amount || 0).toFixed(2)}</td>
                              <td className="text-center">
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <button 
                                    className="btn btn-sm btn-icon btn-ghost-primary" 
                                    title="View Invoice Details"
                                    onClick={() => handleViewInvoice(tx)}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                    title="Print Thermal Receipt"
                                    onClick={() => handlePrintTransaction(tx)}
                                  >
                                    <IconPrinter size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {(!shiftSummary?.transactions || shiftSummary.transactions.length === 0) && (
                            <tr>
                              <td colSpan={7} className="text-center text-muted py-4">No transactions recorded in this shift yet.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <IconClock size={48} className="text-muted mb-3 opacity-50" />
                    <h4 className="fw-bold text-dark">No Shift Currently Open</h4>
                    <p className="text-muted small mb-3">Open a new shift to track cash drawer balance, cashier accountability, and print closing Z-reports.</p>
                    <button 
                      type="button"
                      className="btn btn-success fw-bold shadow-xs"
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                      onClick={() => {
                        setShiftStartFloat('');
                        setShiftStartNote('');
                        setIsStartShiftModalOpen(true);
                      }}
                    >
                      <IconPlayerPlay size={16} className="me-1" /> Start Shift Now
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Closed Shifts History Card */}
            <div className="card shadow-sm border-0 bg-white">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="card-title fw-bold text-dark m-0">Closed Shifts History</h3>
                  <div className="small text-muted">Audited cashier shifts, opening floats, closing cash, and cash differences</div>
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => mutateClosedShifts()}>
                  <IconRefresh size={15} className="me-1" /> Refresh
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter table-hover table-striped">
                  <thead className="table-light">
                    <tr>
                      <th>Shift #</th>
                      <th>Cashier</th>
                      <th>Business Date</th>
                      <th>Opened</th>
                      <th>Closed</th>
                      <th className="text-end">Opening Float</th>
                      <th className="text-end">Expected Cash</th>
                      <th className="text-end">Actual Cash</th>
                      <th className="text-end">Difference</th>
                      <th>Status</th>
                      <th className="text-center" style={{ width: '110px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(closedShifts || []).map((s, idx) => {
                      const diff = Number(s.cash_difference || 0);
                      return (
                        <tr key={idx}>
                          <td className="fw-bold">#{s.id}</td>
                          <td>
                            <div className="fw-medium">{s.cashier_name || 'Cashier'}</div>
                          </td>
                          <td>{s.business_date || '-'}</td>
                          <td className="small text-muted">{s.opened_at ? formatDisplayDateTime(s.opened_at) : '-'}</td>
                          <td className="small text-muted">{s.closed_at ? formatDisplayDateTime(s.closed_at) : '-'}</td>
                          <td className="text-end">{currency} {Number(s.opening_cash || 0).toFixed(2)}</td>
                          <td className="text-end">{currency} {Number(s.expected_cash || 0).toFixed(2)}</td>
                          <td className="text-end fw-bold">{currency} {Number(s.closing_cash || 0).toFixed(2)}</td>
                          <td className="text-end">
                            {diff === 0 ? (
                              <span className="badge bg-success-lt">Balanced</span>
                            ) : diff > 0 ? (
                              <span className="badge bg-warning-lt text-warning fw-bold">+{currency} {diff.toFixed(2)}</span>
                            ) : (
                              <span className="badge bg-danger-lt text-danger fw-bold">-{currency} {Math.abs(diff).toFixed(2)}</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${s.status === 'active' ? 'bg-success-lt' : 'bg-secondary-lt'}`}>
                              {s.status}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <button 
                                className="btn btn-sm btn-icon btn-ghost-primary"
                                title="Inspect Shift Details"
                                onClick={() => setSelectedShiftForDetails(s)}
                              >
                                <IconEye size={16} />
                              </button>
                              <button 
                                className="btn btn-sm btn-outline-info px-1 py-0"
                                title="Print Shift Open Report (Float & Denominations)"
                                onClick={() => openPrintReport('shift_open', s)}
                                style={{ fontSize: '11px', height: '24px', lineHeight: '22px' }}
                              >
                                <IconPrinter size={12} className="me-1" /> Open
                              </button>
                              <button 
                                className="btn btn-sm btn-outline-primary px-1 py-0"
                                title="Print Shift Close Z-Report"
                                onClick={() => openPrintReport('shift_close', s)}
                                style={{ fontSize: '11px', height: '24px', lineHeight: '22px' }}
                              >
                                <IconPrinter size={12} className="me-1" /> Close
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {(!closedShifts || closedShifts.length === 0) && (
                      <tr>
                        <td colSpan={11} className="text-center text-muted py-5">
                          No closed shifts found in database.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: REGISTER CONTROL */}
        {activeTab === 'registers' && (
          <div className="mb-4">
            <div className="card shadow-sm border-0 mb-3 bg-white">
              <div className="card-body p-3">
                <div className="row g-2 align-items-center">
                  <div className="col-auto">
                    <span className="fw-bold me-2 small text-muted text-uppercase">Select Register Date:</span>
                    <input 
                      type="date" 
                      className="form-control form-control-sm d-inline-block" 
                      style={{ width: '150px' }}
                      value={selectedRegisterDate}
                      onChange={(e) => setSelectedRegisterDate(e.target.value)}
                    />
                  </div>
                  <div className="col-auto">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => mutateByDateReg()}>
                      <IconRefresh size={15} className="me-1" /> Refresh Register
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Register Summary Card */}
            <div className="card shadow-sm border-0 mb-4 bg-white">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="card-title fw-bold text-dark m-0">Business Register — {formatDisplayDate(selectedRegisterDate)}</h3>
                  <div className="small text-muted">Daily master cash drawer reconciliation and registered transactions</div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-primary fw-bold shadow-xs"
                    onClick={() => openPrintReport('register', null, registerData)}
                    title="Print Daily Register Audit Report"
                  >
                    <IconPrinter size={15} className="me-1" /> Print Register Report
                  </button>
                  <span className={`badge ${registerData?.register?.status === 'open' ? 'bg-success-lt' : 'bg-secondary-lt'} fs-5 px-3 py-2`}>
                    Status: {registerData?.register?.status?.toUpperCase() || 'OPEN'}
                  </span>
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-sm-6 col-lg-3">
                    <div className="border rounded-3 p-3 bg-light">
                      <div className="text-muted small">Gross Sales</div>
                      <div className="fs-3 fw-bold text-dark">{currency} {Number(registerData?.invoices?.total_gross || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="col-sm-6 col-lg-3">
                    <div className="border rounded-3 p-3 bg-light">
                      <div className="text-muted small">Discount Given</div>
                      <div className="fs-3 fw-bold text-danger">-{currency} {Number(registerData?.invoices?.total_discount || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="col-sm-6 col-lg-3">
                    <div className="border rounded-3 p-3 bg-light">
                      <div className="text-muted small">Total Tax (VAT)</div>
                      <div className="fs-3 fw-bold text-indigo">+{currency} {Number(registerData?.invoices?.total_tax || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="col-sm-6 col-lg-3">
                    <div className="border rounded-3 p-3 bg-light">
                      <div className="text-muted small">Total Billed</div>
                      <div className="fs-3 fw-bold text-primary">{currency} {Number(registerData?.invoices?.total_billed || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="fw-bold m-0 text-dark">Register Transactions</h4>
                  <span className="badge bg-primary-lt">{registerData?.transactions?.length || 0} Transactions</span>
                </div>

                <div className="table-responsive border rounded-2">
                  <table className="table table-vcenter table-hover table-striped m-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Time</th>
                        <th>Invoice / Ref #</th>
                        <th>Customer</th>
                        <th>Payment Method</th>
                        <th className="text-end">Amount</th>
                        <th className="text-center" style={{ width: '100px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(registerData?.transactions || []).map((t, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td className="small text-muted">{formatDisplayTime(t.created_at || t.time)}</td>
                          <td className="fw-bold">{t.invoice_no || `INV-${t.id}`}</td>
                          <td>{t.customer_name || 'Walk-in'}</td>
                          <td><span className="badge bg-secondary-lt">{t.payment_method_name || 'Cash'}</span></td>
                          <td className="text-end fw-bold">{currency} {Number(t.total || t.amount || 0).toFixed(2)}</td>
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <button 
                                className="btn btn-sm btn-icon btn-ghost-primary" 
                                title="View Details"
                                onClick={() => handleViewInvoice(t)}
                              >
                                <IconEye size={16} />
                              </button>
                              <button 
                                className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                title="Print Receipt"
                                onClick={() => handlePrintTransaction(t)}
                              >
                                <IconPrinter size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!registerData?.transactions || registerData.transactions.length === 0) && (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">No transactions found for this business register.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: TRANSACTION REPORTS */}
        {activeTab === 'transactions' && (
          <div className="card shadow-sm border-0 mb-4 bg-white p-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 border-bottom pb-3">
              <div className="d-flex align-items-center gap-2">
                <button 
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 shadow-xs"
                  onClick={() => setActiveTab('daily')}
                  title="Return to Summary Report"
                >
                  <IconArrowLeft size={16} /> Back to Summary
                </button>
                <div>
                  <h3 className="fw-bold m-0 text-dark">{txFilterTitle}</h3>
                  <div className="small text-muted">
                    Period: {formatDisplayDate(fromDate)} {fromDate !== toDate ? ` — ${formatDisplayDate(toDate)}` : ''}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary-lt fs-5 px-3 py-2">
                  {filteredTransactions.length} Transactions
                </span>
                <span className="badge bg-success-lt fs-5 px-3 py-2 fw-bold">
                  {currency} {filteredTransactionsTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <button className="btn btn-sm btn-outline-primary ms-2" onClick={() => window.print()}>
                  <IconPrinter size={16} className="me-1" /> Print List
                </button>
              </div>
            </div>

            {/* Quick Filter Switcher Pills */}
            <div className="d-flex flex-wrap gap-1 mb-3 pb-2 border-bottom">
              {[
                { key: 'all', label: 'All Invoices' },
                { key: 'gross', label: 'Gross Sales' },
                { key: 'net', label: 'Net Sales' },
                { key: 'discount', label: 'Discounted' },
                { key: 'tax', label: 'VAT / Tax' },
                { key: 'round_off', label: 'Round Off' },
                { key: 'complementary', label: 'Complementary' },
                { key: 'due', label: dueInvoicesCount > 0 ? `Sales Due (${dueInvoicesCount})` : 'Sales Due' },
                { key: 'due_collected', label: 'Previous Due Collected' },
                { key: 'room_revenue', label: roomChargesCount > 0 ? `Room Charges (${roomChargesCount})` : 'Room Charges' },
                { key: 'cash', label: 'Cash Only' },
                { key: 'card', label: 'Card' },
                { key: 'online', label: 'Online / QR' },
              ].map(pill => (
                <button
                  key={pill.key}
                  className={`btn btn-sm ${txFilter === pill.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    setTxFilter(pill.key);
                    setTxFilterTitle(pill.label === 'All Invoices' ? 'All Financial Transactions' : `${pill.label} Transactions`);
                  }}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {/* Search Bar */}
            <div className="input-icon mb-3">
              <span className="input-icon-addon">
                <IconSearch size={18} />
              </span>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search invoice number, customer, payment method or item..." 
                value={txSearch}
                onChange={(e) => setTxSearch(e.target.value)}
              />
            </div>

            <div className="table-responsive">
              <table className="table table-vcenter table-hover table-striped">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Invoice / Ref #</th>
                    <th>Date / Time</th>
                    <th>Customer</th>
                    <th>Payment</th>
                    <th className="text-end">Subtotal</th>
                    <th className="text-end">Tax</th>
                    <th className="text-end">Discount</th>
                    <th className="text-end">Round Off</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Due</th>
                    <th>Status</th>
                    <th className="text-center" style={{ width: '100px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((inv, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td className="fw-bold">
                        {inv.invoice_no || (inv.token_no ? `TOKEN-#${inv.token_no}` : `INV-${inv.id}`)}
                        {inv.token_no && (
                          <span className="badge bg-blue-lt ms-1 font-monospace">#{inv.token_no}</span>
                        )}
                      </td>
                      <td className="small text-muted">
                        {inv.created_at || inv.date ? formatDisplayDateTime(inv.created_at || inv.date) : '-'}
                      </td>
                      <td>{inv.customer_name || 'Walk-in Customer'}</td>
                      <td>
                        <span className="badge bg-secondary-lt">
                          {inv.payment_method_name || 'Cash'}
                        </span>
                      </td>
                      <td className="text-end">{currency} {Number(inv.sub_total || inv.total || 0).toFixed(2)}</td>
                      <td className="text-end">{currency} {Number(inv.tax_total || 0).toFixed(2)}</td>
                      <td className="text-end text-danger">
                        {Number(inv.discount || 0) > 0 ? `-${Number(inv.discount).toFixed(2)}` : '0.00'}
                      </td>
                      <td className="text-end text-muted">
                        {currency} {Number(inv.round_off || 0).toFixed(2)}
                      </td>
                      <td className="text-end fw-bold text-dark">
                        {currency} {Number(inv.total || 0).toFixed(2)}
                      </td>
                      <td className="text-end text-warning fw-bold">
                        {Number(inv.due_amount || 0) > 0 ? `${currency} ${Number(inv.due_amount).toFixed(2)}` : '-'}
                      </td>
                      <td>
                        <span className={`badge ${inv.payment_status === 'paid' ? 'bg-success-lt' : 'bg-warning-lt'}`}>
                          {inv.payment_status || (Number(inv.due_amount || 0) > 0 ? 'due' : 'completed')}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <button 
                            className="btn btn-sm btn-icon btn-ghost-primary"
                            title="View Transaction Details"
                            onClick={() => handleViewInvoice(inv)}
                          >
                            <IconEye size={16} />
                          </button>
                          <button 
                            className="btn btn-sm btn-icon btn-ghost-secondary ms-1"
                            title="Print Thermal Receipt"
                            onClick={() => handlePrintTransaction(inv)}
                          >
                            <IconPrinter size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredTransactions.length === 0 && (
                    <tr>
                      <td colSpan={13} className="text-center text-muted py-5">
                        No transactions found matching this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MODAL 0: DRILL-DOWN TRANSACTIONS MODAL (When clicking 👁 on Gross Sales, Net Sales, Tax, Discount, Cash, etc.) */}
        {drillModal.isOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setDrillModal(prev => ({ ...prev, isOpen: false }))}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-6xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconReceipt size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      {drillModal.title}
                    </h3>
                    <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                      Period: {formatDisplayDate(fromDate)} {fromDate !== toDate ? ` — ${formatDisplayDate(toDate)}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white/15 text-white border border-white/20">
                    {modalDrillItems.length} Records
                  </span>
                  <span className="px-3.5 py-1.5 rounded-xl text-xs font-black bg-[#70B56A] text-white shadow-sm">
                    {currency} {modalDrillTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <button 
                    type="button" 
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95 ml-1"
                    onClick={() => setDrillModal(prev => ({ ...prev, isOpen: false }))}
                    title="Close"
                  >
                    <IconX size={20} />
                  </button>
                </div>
              </div>

              {/* Quick Search */}
              <div className="p-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/50 shrink-0">
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400">
                    <IconSearch size={18} />
                  </span>
                  <input 
                    type="text" 
                    className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#70B56A] transition" 
                    placeholder="Search invoice number, customer name, payment method..." 
                    value={drillSearch}
                    onChange={(e) => setDrillSearch(e.target.value)}
                    autoFocus
                  />
                  {drillSearch && (
                    <button 
                      type="button" 
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition"
                      onClick={() => setDrillSearch('')}
                    >
                      <IconX size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Table Body */}
              <div className="overflow-y-auto flex-1 p-0">
                <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
                  <thead className="sticky top-0 bg-zinc-100 dark:bg-zinc-800/90 backdrop-blur-xs text-zinc-600 dark:text-zinc-300 uppercase tracking-wider font-bold border-b border-zinc-200 dark:border-zinc-700 z-10">
                    <tr>
                      <th className="py-3 px-3.5 text-center" style={{ width: '40px' }}>#</th>
                      <th className="py-3 px-3">Invoice / Ref #</th>
                      <th className="py-3 px-3">Date & Time</th>
                      <th className="py-3 px-3">Customer</th>
                      <th className="py-3 px-3">Payment</th>
                      <th className="py-3 px-3 text-end">Subtotal</th>
                      <th className="py-3 px-3 text-end">Tax</th>
                      <th className="py-3 px-3 text-end">Discount</th>
                      <th className="py-3 px-3 text-end">Total</th>
                      <th className="py-3 px-3 text-end">Due</th>
                      <th className="py-3 px-3 text-center">Status</th>
                      <th className="py-3 px-3 text-center" style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {modalDrillItems.map((inv, idx) => (
                      <tr key={idx} className="hover:bg-emerald-50/40 dark:hover:bg-zinc-800/50 transition-colors">
                        <td className="py-3 px-3.5 text-center text-zinc-400">{idx + 1}</td>
                        <td className="py-3 px-3 font-bold text-zinc-900 dark:text-white">
                          {inv.invoice_no || (inv.token_no ? `TOKEN-#${inv.token_no}` : `INV-${inv.id}`)}
                          {inv.token_no && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-[#243922] font-mono text-[10px] font-bold">
                              #{inv.token_no}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-zinc-500 text-[11px]">
                          {inv.created_at || inv.date ? formatDisplayDateTime(inv.created_at || inv.date) : '-'}
                        </td>
                        <td className="py-3 px-3 font-medium text-zinc-800 dark:text-zinc-200">
                          {inv.customer_name || 'Walk-in Customer'}
                        </td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                            {inv.payment_method_name || 'Cash'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-end text-zinc-600 dark:text-zinc-400">
                          {currency} {Number(inv.sub_total || inv.total || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-3 text-end text-zinc-600 dark:text-zinc-400">
                          {currency} {Number(inv.tax_total || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-3 text-end text-rose-600 font-medium">
                          {Number(inv.discount || 0) > 0 ? `-${Number(inv.discount).toFixed(2)}` : '0.00'}
                        </td>
                        <td className="py-3 px-3 text-end font-bold text-zinc-900 dark:text-white">
                          {currency} {Number(inv.total || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-3 text-end font-bold text-amber-600">
                          {Number(inv.due_amount || 0) > 0 ? `${currency} ${Number(inv.due_amount).toFixed(2)}` : '-'}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                            inv.payment_status === 'paid' 
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                          }`}>
                            {inv.payment_status || (Number(inv.due_amount || 0) > 0 ? 'due' : 'completed')}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button 
                              type="button"
                              className="p-1.5 rounded-xl text-[#243922] dark:text-emerald-300 bg-[#ECF1EB] dark:bg-emerald-950/60 hover:bg-[#DCE7DB] dark:hover:bg-emerald-900 border border-[#DCE7DB] dark:border-emerald-800/40 transition active:scale-95 shadow-xs"
                              title="View Order Breakdown & Items (👁)"
                              onClick={() => handleViewInvoice(inv)}
                            >
                              <IconEye size={16} />
                            </button>
                            <button 
                              type="button"
                              className="p-1.5 rounded-xl text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition active:scale-95 shadow-xs"
                              title="Print Thermal Receipt (🖨)"
                              onClick={() => handlePrintTransaction(inv)}
                            >
                              <IconPrinter size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {modalDrillItems.length === 0 && (
                      <tr>
                        <td colSpan={12} className="text-center text-zinc-400 py-12 text-sm">
                          No transactions found matching your criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between flex-wrap gap-3 shrink-0">
                <button 
                  type="button" 
                  className="px-4 py-2 rounded-xl text-xs font-bold text-[#243922] dark:text-emerald-400 bg-[#ECF1EB] dark:bg-zinc-800 hover:bg-[#DCE7DB] border border-[#DCE7DB] dark:border-zinc-700 transition flex items-center gap-1.5 active:scale-95"
                  onClick={() => {
                    setDrillModal(prev => ({ ...prev, isOpen: false }));
                    setActiveTab('transactions');
                    setTxFilter(drillModal.type);
                    setTxFilterTitle(drillModal.title);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <IconReceipt size={16} />
                  Open in Full Transaction Reports Tab
                </button>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => openPrintReport('sales')}
                  >
                    <IconPrinter size={16} />
                    Print Sales Report
                  </button>
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setDrillModal(prev => ({ ...prev, isOpen: false }))}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 1: MULTI-CURRENCY DENOMINATIONS & CURRENCY CONVERTER DRAWER */}
        {isDenomDrawerOpen && (
          <div 
            className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsDenomDrawerOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-4xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconCalculator size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Multi-Currency Cash Denomination Calculator
                    </h3>
                    <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                      Count physical cash notes & coins for drawer reconciliation
                    </p>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsDenomDrawerOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                {/* Currency Selection Tabs */}
                <div className="flex items-center justify-between flex-wrap gap-3 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                  <div className="flex flex-wrap gap-1.5 p-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
                    {availableCurrencies.map(curr => (
                      <button
                        key={curr.code}
                        type="button"
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                          activeCurrency.code === curr.code
                            ? 'bg-[#70B56A] text-white shadow-sm'
                            : 'text-zinc-600 dark:text-zinc-300 hover:text-zinc-900'
                        }`}
                        onClick={() => setSelectedCurrencyCode(curr.code)}
                      >
                        <span>{curr.code} ({curr.symbol})</span>
                        {curr.is_base && <span className="text-[10px] bg-white/20 px-1 rounded">Base</span>}
                      </button>
                    ))}
                  </div>

                  {/* Live Exchange Rate / Converter Input */}
                  {!activeCurrency.is_base && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 text-xs">
                      <IconExchange size={15} className="text-zinc-400" />
                      <span className="text-zinc-500 font-medium">Exchange Rate to Base:</span>
                      <input 
                        type="number" 
                        step="any"
                        className="w-20 px-2 py-1 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-600 rounded-lg text-end font-bold text-zinc-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#70B56A]" 
                        value={activeExchangeRate}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 1.0;
                          setCustomRates(prev => ({ ...prev, [activeCurrency.code]: val }));
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Notes & Coins Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {(activeCurrency.denominations || []).map(d => {
                    const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                    const numCount = Number(count) || 0;
                    const lineTotal = numCount * Number(d);
                    return (
                      <div 
                        key={d}
                        className={`p-3 rounded-2xl border transition-all ${
                          numCount > 0 
                            ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 shadow-xs' 
                            : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-[90px]">
                            <div className="text-lg font-black text-zinc-900 dark:text-white flex items-center gap-1">
                              <span>{activeCurrency.symbol}{d}</span>
                              <span className="text-[10px] font-semibold text-zinc-400 uppercase">{activeCurrency.code}</span>
                            </div>
                            <div className="text-[11px] text-zinc-500">
                              {numCount} × {d}
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            <button 
                              type="button" 
                              className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-200 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                            >
                              -1
                            </button>
                            <input 
                              type="number" 
                              min="0"
                              className="w-16 h-7 text-center font-black text-sm bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-600 rounded-lg text-zinc-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#70B56A]"
                              value={count}
                              placeholder="0"
                              onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                            />
                            <button 
                              type="button" 
                              className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-200 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                            >
                              +1
                            </button>
                            <button 
                              type="button" 
                              className="px-1.5 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-[#ECF1EB] dark:bg-emerald-950/60 hover:bg-[#DCE7DB] text-[#243922] dark:text-emerald-300 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 5)}
                            >
                              +5
                            </button>
                            <button 
                              type="button" 
                              className="px-1.5 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-[#ECF1EB] dark:bg-emerald-950/60 hover:bg-[#DCE7DB] text-[#243922] dark:text-emerald-300 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 10)}
                            >
                              +10
                            </button>
                          </div>

                          <div className="text-end min-w-[80px]">
                            <div className="text-xs font-black text-zinc-900 dark:text-white">
                              {activeCurrency.symbol} {lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary Footer Box */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs text-zinc-500">{activeCurrency.code} Subtotal:</div>
                    <div className="text-xl font-black text-zinc-900 dark:text-white">
                      {activeCurrency.symbol} {Number(denominationCalculations.perCurrency[activeCurrency.code]?.currTotal || 0).toFixed(2)}
                    </div>
                    {!activeCurrency.is_base && (
                      <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-0.5">
                        Converted Base Value: {currency} {Number(denominationCalculations.perCurrency[activeCurrency.code]?.baseTotal || 0).toFixed(2)}
                      </div>
                    )}
                  </div>

                  <div className="text-end">
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Grand Drawer Cash (Base Currency):</div>
                    <div className="text-2xl sm:text-3xl font-black text-[#70B56A]">
                      {currency} {denominationCalculations.grandBaseTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between flex-wrap gap-3 shrink-0">
                <button 
                  type="button" 
                  className="px-3.5 py-2 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 border border-rose-200 dark:border-rose-900 transition active:scale-95"
                  onClick={() => handleClearDenominations(activeCurrency.code)}
                >
                  Clear {activeCurrency.code} Counts
                </button>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setIsDenomDrawerOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={handleApplyDenominations}
                  >
                    <IconCheck size={16} />
                    {denomTarget === 'start_shift' ? 'Apply to Opening Float' : 
                     denomTarget === 'close_shift' ? 'Apply to Closing Cash' : 
                     denomTarget === 'opening_balance' ? 'Apply to Day Book Float' : 'Confirm Counted Cash'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 2: START SHIFT MODAL */}
        {isStartShiftModalOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsStartShiftModalOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-2xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconPlayerPlay size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Start Cashier Shift
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                        <IconUser size={13} /> {staffProfileName}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                        <IconCalendar size={13} /> {formatDisplayDate(fromDate)}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsStartShiftModalOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleStartShiftSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                  {/* Mode Switcher */}
                  <div className="flex p-1 bg-zinc-100 dark:bg-zinc-800 rounded-2xl">
                    <button
                      type="button"
                      className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        startShiftTab === 'quick' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setStartShiftTab('quick')}
                    >
                      <IconCash size={16} />
                      <span>⚡ Quick Float Amount</span>
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        startShiftTab === 'denominations' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setStartShiftTab('denominations')}
                    >
                      <IconCalculator size={16} />
                      <span>🧮 Count Notes ({currency})</span>
                    </button>
                  </div>

                  {/* QUICK FLOAT MODE */}
                  {startShiftTab === 'quick' ? (
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-2">
                        Opening Cash Float ({currency})
                      </label>
                      <div className="relative flex items-center mb-3">
                        <span className="absolute left-4 font-bold text-lg text-zinc-500 dark:text-zinc-400">
                          {currency}
                        </span>
                        <input 
                          type="number" 
                          step="any"
                          required
                          className="w-full pl-16 pr-4 py-3.5 bg-zinc-50 dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl text-2xl font-black text-zinc-900 dark:text-white focus:outline-none focus:border-[#70B56A] focus:ring-4 focus:ring-[#70B56A]/15 transition"
                          placeholder="e.g. 5000"
                          value={shiftStartFloat}
                          onChange={(e) => setShiftStartFloat(e.target.value)}
                          autoFocus
                        />
                      </div>

                      {/* Quick Presets */}
                      <div>
                        <div className="text-xs font-semibold text-zinc-500 mb-2">Quick Presets / Add Amount:</div>
                        <div className="flex flex-wrap gap-2">
                          {[500, 1000, 2000, 5000, 10000, 20000].map(amt => (
                            <button
                              key={amt}
                              type="button"
                              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-[#ECF1EB] hover:bg-[#DCE7DB] text-[#243922] border border-[#DCE7DB] transition active:scale-95"
                              onClick={() => {
                                const current = parseFloat(shiftStartFloat) || 0;
                                setShiftStartFloat(String(current + amt));
                              }}
                            >
                              +{currency} {amt.toLocaleString()}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="px-3 py-1.5 rounded-xl text-xs font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition active:scale-95"
                            onClick={() => setShiftStartFloat('')}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* INTEGRATED DENOMINATIONS MODE */
                    <div className="space-y-4">
                      {/* Currency Tabs */}
                      <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                        <div className="flex flex-wrap gap-1 p-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
                          {availableCurrencies.map(curr => (
                            <button
                              key={curr.code}
                              type="button"
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                                activeCurrency.code === curr.code
                                  ? 'bg-[#70B56A] text-white shadow-sm'
                                  : 'text-zinc-600 dark:text-zinc-300'
                              }`}
                              onClick={() => setSelectedCurrencyCode(curr.code)}
                            >
                              {curr.code} ({curr.symbol}) {curr.is_base ? '★' : ''}
                            </button>
                          ))}
                        </div>
                        <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
                          Counted Total: {currency} {denominationCalculations.grandBaseTotal.toFixed(2)}
                        </div>
                      </div>

                      {/* Denominations Steppers Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1">
                        {(activeCurrency.denominations || []).map(d => {
                          const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                          const numCount = Number(count) || 0;
                          const lineTotal = numCount * Number(d);
                          return (
                            <div 
                              key={d}
                              className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 transition ${
                                numCount > 0 ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800' : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                              }`}
                            >
                              <div className="min-w-[70px]">
                                <span className="font-black text-sm text-zinc-900 dark:text-white">{activeCurrency.symbol}{d}</span>
                                <div className="text-[10px] text-zinc-500">{activeCurrency.code}</div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                                >
                                  -
                                </button>
                                <input 
                                  type="number" 
                                  min="0"
                                  className="w-12 h-6 text-center font-black text-xs bg-white dark:bg-zinc-900 border border-zinc-300 rounded text-zinc-900 dark:text-white"
                                  value={count}
                                  placeholder="0"
                                  onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                                />
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                                >
                                  +
                                </button>
                                <button 
                                  type="button" 
                                  className="px-1.5 h-6 rounded flex items-center justify-center font-bold text-[10px] bg-[#ECF1EB] text-[#243922] transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 5)}
                                >
                                  +5
                                </button>
                              </div>
                              <div className="text-end min-w-[65px]">
                                <span className="text-xs font-bold text-zinc-900 dark:text-white">{lineTotal.toFixed(0)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="p-3 rounded-2xl bg-[#ECF1EB] dark:bg-zinc-800 border border-[#DCE7DB] dark:border-zinc-700 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#243922] dark:text-emerald-300">Total Counted Float:</span>
                        <span className="text-lg font-black text-[#243922] dark:text-emerald-300">
                          {currency} {denominationCalculations.grandBaseTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Remarks Input */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-1.5">
                      Shift Notes / Float Remarks (Optional)
                    </label>
                    <textarea 
                      className="w-full px-3.5 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#70B56A]" 
                      rows={2} 
                      placeholder="e.g. Morning shift float assigned from main safe."
                      value={shiftStartNote}
                      onChange={(e) => setShiftStartNote(e.target.value)}
                    ></textarea>
                  </div>

                  {/* Security/Notice alert */}
                  <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 text-xs text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
                    <IconCheck size={16} className="text-emerald-600 shrink-0" />
                    <span>Drawer session will be opened for <strong>{staffProfileName}</strong> on <strong>{formatDisplayDate(fromDate)}</strong>.</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-end gap-2 shrink-0">
                  <button 
                    type="button" 
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setIsStartShiftModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-lg shadow-[#70B56A]/20 transition flex items-center gap-1.5 active:scale-95"
                    disabled={isStartingShift}
                  >
                    <IconPlayerPlay size={16} />
                    {isStartingShift ? 'Opening Shift...' : 'Confirm & Start Shift'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL 3: CLOSE SHIFT MODAL */}
        {isCloseShiftModalOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsCloseShiftModalOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-2xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-rose-300 border border-white/10 shadow-inner">
                    <IconPlayerStop size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      End & Reconcile Shift #{activeShift?.id || activeShift?.shift_number}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                        <IconUser size={13} /> {activeShift?.cashier_name || activeShift?.opened_by_name || staffProfileName}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                        <IconClock size={13} /> Opened {formatDisplayTime(activeShift?.opened_at)}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsCloseShiftModalOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleCloseShiftSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4">
                  {/* Expected Drawer Cash Breakdown Card */}
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 space-y-2">
                    <div className="flex items-center justify-between text-xs text-zinc-600 dark:text-zinc-400">
                      <span>Opening Float:</span>
                      <span className="font-bold text-zinc-900 dark:text-white">{currency} {activeShiftOpening.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-emerald-600 font-medium">
                      <span>+ Cash Sales Collected:</span>
                      <span className="font-bold">+{currency} {activeShiftCashSales.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-rose-600 font-medium">
                      <span>- Cash Expenses Paid:</span>
                      <span className="font-bold">-{currency} {activeShiftCashExpenses.toFixed(2)}</span>
                    </div>
                    <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-300">
                        Expected Drawer Cash:
                      </span>
                      <span className="text-base font-black text-[#243922] dark:text-emerald-400">
                        {currency} {activeShiftExpectedCash.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Mode Switcher */}
                  <div className="flex p-1 bg-zinc-100 dark:bg-zinc-800 rounded-2xl">
                    <button
                      type="button"
                      className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        closeShiftTab === 'quick' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setCloseShiftTab('quick')}
                    >
                      <IconCash size={15} />
                      <span>⚡ Enter Actual Cash</span>
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        closeShiftTab === 'denominations' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setCloseShiftTab('denominations')}
                    >
                      <IconCalculator size={15} />
                      <span>🧮 Count Notes in Drawer</span>
                    </button>
                  </div>

                  {/* ACTUAL CASH INPUT MODE */}
                  {closeShiftTab === 'quick' ? (
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-2">
                        Actual Physical Cash In Hand ({currency})
                      </label>
                      <div className="relative flex items-center">
                        <span className="absolute left-4 font-bold text-lg text-zinc-500 dark:text-zinc-400">
                          {currency}
                        </span>
                        <input 
                          type="number" 
                          step="any"
                          required
                          className="w-full pl-16 pr-4 py-3.5 bg-zinc-50 dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl text-2xl font-black text-zinc-900 dark:text-white focus:outline-none focus:border-[#70B56A] focus:ring-4 focus:ring-[#70B56A]/15 transition"
                          placeholder="e.g. 15200"
                          value={shiftCloseCash}
                          onChange={(e) => setShiftCloseCash(e.target.value)}
                          autoFocus
                        />
                      </div>
                    </div>
                  ) : (
                    /* INTEGRATED DENOMINATIONS MODE */
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[190px] overflow-y-auto pr-1">
                        {(activeCurrency.denominations || []).map(d => {
                          const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                          const numCount = Number(count) || 0;
                          const lineTotal = numCount * Number(d);
                          return (
                            <div 
                              key={d}
                              className={`p-2 rounded-xl border flex items-center justify-between gap-2 transition ${
                                numCount > 0 ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800' : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                              }`}
                            >
                              <div className="min-w-[65px]">
                                <span className="font-black text-xs text-zinc-900 dark:text-white">{activeCurrency.symbol}{d}</span>
                                <div className="text-[10px] text-zinc-500">{activeCurrency.code}</div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                                >
                                  -
                                </button>
                                <input 
                                  type="number" 
                                  min="0"
                                  className="w-11 h-6 text-center font-black text-xs bg-white dark:bg-zinc-900 border border-zinc-300 rounded text-zinc-900 dark:text-white"
                                  value={count}
                                  placeholder="0"
                                  onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                                />
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                                >
                                  +
                                </button>
                                <button 
                                  type="button" 
                                  className="px-1.5 h-6 rounded flex items-center justify-center font-bold text-[10px] bg-[#ECF1EB] text-[#243922] transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 5)}
                                >
                                  +5
                                </button>
                              </div>
                              <div className="text-end min-w-[55px]">
                                <span className="text-xs font-bold text-zinc-900 dark:text-white">{lineTotal.toFixed(0)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="p-2.5 rounded-xl bg-[#ECF1EB] dark:bg-zinc-800 border border-[#DCE7DB] dark:border-zinc-700 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#243922] dark:text-emerald-300">Total Counted Drawer Cash:</span>
                        <span className="text-base font-black text-[#243922] dark:text-emerald-300">
                          {currency} {denominationCalculations.grandBaseTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Live Reconciliation Difference Status */}
                  <div>
                    {activeShiftDiff === 0 ? (
                      <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 font-bold text-xs text-center flex items-center justify-center gap-2">
                        <IconCheck size={18} className="text-emerald-600" />
                        <span>✓ PERFECTLY BALANCED (Drawer cash matches expected balance)</span>
                      </div>
                    ) : activeShiftDiff > 0 ? (
                      <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 font-bold text-xs text-center flex items-center justify-center gap-2">
                        <IconAlertTriangle size={18} className="text-amber-600" />
                        <span>⚠ CASH OVERAGE: +{currency} {activeShiftDiff.toFixed(2)} (Actual exceeds expected)</span>
                      </div>
                    ) : (
                      <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300 font-bold text-xs text-center flex items-center justify-center gap-2">
                        <IconAlertTriangle size={18} className="text-rose-600" />
                        <span>⚠ CASH SHORTAGE: -{currency} {Math.abs(activeShiftDiff).toFixed(2)} (Shortage in drawer)</span>
                      </div>
                    )}
                  </div>

                  {/* Closing Remarks */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-1.5">
                      Closing Remarks / Handover Notes
                    </label>
                    <textarea 
                      className="w-full px-3.5 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#70B56A]" 
                      rows={2} 
                      placeholder="Any comments regarding drawer closing or petty cash hand-over."
                      value={shiftCloseNote}
                      onChange={(e) => setShiftCloseNote(e.target.value)}
                    ></textarea>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-end gap-2 shrink-0">
                  <button 
                    type="button" 
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setIsCloseShiftModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-600/20 transition flex items-center gap-1.5 active:scale-95"
                    disabled={isClosingShift}
                  >
                    <IconPlayerStop size={16} />
                    {isClosingShift ? 'Closing Shift...' : 'Confirm & Close Shift'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL 4: SHIFT DETAILS / Z-REPORT MODAL */}
        {selectedShiftForDetails && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setSelectedShiftForDetails(null)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-4xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconReceipt size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Shift Z-Report & Audit Summary #{selectedShiftForDetails.id}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                        <IconUser size={13} /> {selectedShiftForDetails.cashier_name || selectedShiftForDetails.opened_by_name || 'Cashier'}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                        <IconCalendar size={13} /> {selectedShiftForDetails.business_date || formatDisplayDate(fromDate)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-lg font-bold text-[10px] uppercase ${
                        selectedShiftForDetails.closed_at 
                          ? 'bg-zinc-800 text-zinc-300 border border-zinc-700' 
                          : 'bg-emerald-500 text-white'
                      }`}>
                        {selectedShiftForDetails.closed_at ? 'Closed' : 'Active Session'}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setSelectedShiftForDetails(null)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                {/* 4 Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Opening Float</div>
                    <div className="text-lg sm:text-xl font-black text-zinc-900 dark:text-white">
                      {currency} {Number(selectedShiftForDetails.opening_balance || selectedShiftForDetails.opening_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Expected Cash</div>
                    <div className="text-lg sm:text-xl font-black text-[#243922] dark:text-emerald-400">
                      {currency} {Number(selectedShiftForDetails.system_expected_cash || selectedShiftForDetails.expected_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Actual Cash In Hand</div>
                    <div className="text-lg sm:text-xl font-black text-emerald-600">
                      {currency} {Number(selectedShiftForDetails.actual_cash_in_hand || selectedShiftForDetails.closing_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs font-semibold mb-1 text-zinc-500">Difference / Variance</div>
                    <div className={`text-lg sm:text-xl font-black ${
                      Number(selectedShiftForDetails.cash_difference || 0) === 0 
                        ? 'text-emerald-600' 
                        : Number(selectedShiftForDetails.cash_difference || 0) > 0 
                          ? 'text-amber-600' 
                          : 'text-rose-600'
                    }`}>
                      {Number(selectedShiftForDetails.cash_difference || 0) > 0 ? '+' : ''}
                      {currency} {Number(selectedShiftForDetails.cash_difference || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Timestamps Card */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Opened At:</span>
                    <span className="font-bold text-zinc-900 dark:text-white">
                      {selectedShiftForDetails.opened_at ? formatDisplayDateTime(selectedShiftForDetails.opened_at) : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Closed At:</span>
                    <span className="font-bold text-zinc-900 dark:text-white">
                      {selectedShiftForDetails.closed_at ? formatDisplayDateTime(selectedShiftForDetails.closed_at) : 'Currently Active'}
                    </span>
                  </div>
                  {selectedShiftForDetails.closing_notes && (
                    <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
                      <span className="text-zinc-500 block mb-0.5">Closing Remarks:</span>
                      <span className="font-medium text-zinc-800 dark:text-zinc-200 italic">
                        "{selectedShiftForDetails.closing_notes}"
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between flex-wrap gap-2 shrink-0">
                <button 
                  type="button" 
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                  onClick={() => setSelectedShiftForDetails(null)}
                >
                  Close
                </button>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="px-3.5 py-2 rounded-xl text-xs font-bold text-[#243922] dark:text-emerald-300 bg-[#ECF1EB] dark:bg-zinc-800 hover:bg-[#DCE7DB] border border-[#DCE7DB] dark:border-zinc-700 transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => openPrintReport('shift_open', selectedShiftForDetails)}
                  >
                    <IconPrinter size={15} />
                    Print Shift Open
                  </button>
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => openPrintReport('shift_close', selectedShiftForDetails)}
                  >
                    <IconPrinter size={15} />
                    Print Shift Close (Z-Report)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 5: ITEMIZED INVOICE DETAILS MODAL (when clicking 👁 on any transaction) */}
        {viewingInvoice && (
          <div 
            className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setViewingInvoice(null)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-3xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconReceipt size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Invoice #{viewingInvoice.invoiceNo || viewingInvoice.invoice_no || viewingInvoice.id}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                        <IconClock size={13} /> {viewingInvoice.created_at || viewingInvoice.date ? formatDisplayDateTime(viewingInvoice.created_at || viewingInvoice.date) : '-'}
                      </span>
                      {viewingInvoice.staff_name && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                          <IconUser size={13} /> {viewingInvoice.staff_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setViewingInvoice(null)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                {/* Summary row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Customer</div>
                    <div className="font-bold text-sm text-zinc-900 dark:text-white truncate">
                      {viewingInvoice.customer_name || 'Walk-in Customer'}
                    </div>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Payment Method</div>
                    <div className="font-bold text-sm text-zinc-900 dark:text-white">
                      {viewingInvoice.payment_method_name || 'Cash'}
                    </div>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Status</div>
                    <span className={`inline-block px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                      viewingInvoice.payment_status === 'paid' 
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                    }`}>
                      {viewingInvoice.payment_status || 'completed'}
                    </span>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Invoice Total</div>
                    <div className="font-black text-sm text-[#243922] dark:text-emerald-400">
                      {currency} {Number(viewingInvoice.total || viewingInvoice.amount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Items list */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Itemized Breakdown</h4>
                  <div className="border border-zinc-200 dark:border-zinc-700 rounded-2xl overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 font-bold border-b border-zinc-200 dark:border-zinc-700">
                        <tr>
                          <th className="py-2.5 px-3 text-center" style={{ width: '40px' }}>#</th>
                          <th className="py-2.5 px-3">Item</th>
                          <th className="py-2.5 px-3 text-center">Qty</th>
                          <th className="py-2.5 px-3 text-end">Price ({currency})</th>
                          <th className="py-2.5 px-3 text-end">Total ({currency})</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                        {viewingInvoice.orders && viewingInvoice.orders.length > 0 ? (
                          viewingInvoice.orders.flatMap(o => o.items || []).map((it, idx) => (
                            <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                              <td className="py-2.5 px-3 text-center text-zinc-400">{idx + 1}</td>
                              <td className="py-2.5 px-3">
                                <div className="font-bold text-zinc-900 dark:text-white">{it.item_title || it.title}</div>
                                {it.variant_title && <div className="text-[11px] text-zinc-500">Variant: {it.variant_title}</div>}
                                {it.addons && it.addons.length > 0 && (
                                  <div className="text-[11px] text-zinc-500">
                                    Addons: {it.addons.map(a => a.title || a.name).join(', ')}
                                  </div>
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-center font-bold">{it.quantity}</td>
                              <td className="py-2.5 px-3 text-end text-zinc-500">{Number(it.price || 0).toFixed(2)}</td>
                              <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white">{Number((it.price * it.quantity) || 0).toFixed(2)}</td>
                            </tr>
                          ))
                        ) : viewingInvoice.item_name ? (
                          <tr>
                            <td className="py-2.5 px-3 text-center">1</td>
                            <td className="py-2.5 px-3 font-bold text-zinc-900 dark:text-white">{viewingInvoice.item_name}</td>
                            <td className="py-2.5 px-3 text-center font-bold">{viewingInvoice.quantity || 1}</td>
                            <td className="py-2.5 px-3 text-end text-zinc-500">{Number(viewingInvoice.price || 0).toFixed(2)}</td>
                            <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white">{Number(viewingInvoice.total || 0).toFixed(2)}</td>
                          </tr>
                        ) : (
                          <tr>
                            <td className="py-2.5 px-3 text-center">1</td>
                            <td className="py-2.5 px-3 font-bold text-zinc-900 dark:text-white">Order Items (Summary)</td>
                            <td className="py-2.5 px-3 text-center font-bold">1</td>
                            <td className="py-2.5 px-3 text-end text-zinc-500">{Number(viewingInvoice.subtotal || viewingInvoice.sub_total || viewingInvoice.total || 0).toFixed(2)}</td>
                            <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white">{Number(viewingInvoice.subtotal || viewingInvoice.sub_total || viewingInvoice.total || 0).toFixed(2)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Financial Breakdown card */}
                <div className="flex justify-end">
                  <div className="w-full sm:w-80 p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Subtotal:</span>
                      <span className="font-bold text-zinc-900 dark:text-white">{currency} {Number(viewingInvoice.subtotal !== undefined ? viewingInvoice.subtotal : (viewingInvoice.sub_total || 0)).toFixed(2)}</span>
                    </div>
                    {Number(viewingInvoice.discount || 0) > 0 && (
                      <div className="flex justify-between text-rose-600 font-medium">
                        <span>Discount:</span>
                        <span>-{currency} {Number(viewingInvoice.discount).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(viewingInvoice.taxTotal || viewingInvoice.tax_total || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">VAT / Tax:</span>
                        <span className="font-bold text-zinc-900 dark:text-white">+{currency} {Number(viewingInvoice.taxTotal || viewingInvoice.tax_total).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(viewingInvoice.roundOff || viewingInvoice.round_off || 0) !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Round Off:</span>
                        <span className="font-bold text-zinc-900 dark:text-white">{currency} {Number(viewingInvoice.roundOff || viewingInvoice.round_off).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 flex justify-between text-sm font-black">
                      <span className="text-zinc-900 dark:text-white">Total Amount:</span>
                      <span className="text-[#243922] dark:text-emerald-400">{currency} {Number(viewingInvoice.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-600 font-bold">
                      <span>Paid Amount:</span>
                      <span>{currency} {Number(viewingInvoice.paidAmount !== undefined ? viewingInvoice.paidAmount : (viewingInvoice.paid_amount !== undefined ? viewingInvoice.paid_amount : viewingInvoice.total)).toFixed(2)}</span>
                    </div>
                    {Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : (viewingInvoice.due_amount || 0)) > 0 && (
                      <div className="flex justify-between text-amber-600 font-bold">
                        <span>Due Amount:</span>
                        <span>{currency} {Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : viewingInvoice.due_amount).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between shrink-0">
                <button 
                  type="button" 
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                  onClick={() => setViewingInvoice(null)}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                  onClick={() => handlePrintTransaction(viewingInvoice)}
                >
                  <IconPrinter size={16} />
                  Print Thermal Receipt
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 6: SET OPENING BALANCE MODAL */}
        {isOpeningModalOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsOpeningModalOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-lg overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10">
                    <IconCoins size={20} />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-black text-white m-0 tracking-tight">
                      Set Day Book Opening Balance
                    </h3>
                    <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                      Business Date: {formatDisplayDate(fromDate)}
                    </p>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsOpeningModalOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1">
                {/* Mode Switcher */}
                <div className="flex p-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                      openingModalTab === 'quick' 
                        ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                        : 'text-zinc-500 hover:text-zinc-900'
                    }`}
                    onClick={() => setOpeningModalTab('quick')}
                  >
                    <IconCash size={15} />
                    <span>⚡ Quick Amount</span>
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                      openingModalTab === 'denominations' 
                        ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                        : 'text-zinc-500 hover:text-zinc-900'
                    }`}
                    onClick={() => setOpeningModalTab('denominations')}
                  >
                    <IconCalculator size={15} />
                    <span>🧮 Count Notes</span>
                  </button>
                </div>

                {openingModalTab === 'quick' ? (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-2">
                      Opening Cash Amount ({currency})
                    </label>
                    <div className="relative flex items-center mb-3">
                      <span className="absolute left-4 font-bold text-base text-zinc-500">
                        {currency}
                      </span>
                      <input 
                        type="number" 
                        step="any"
                        className="w-full pl-14 pr-4 py-3 bg-zinc-50 dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl text-xl font-black text-zinc-900 dark:text-white focus:outline-none focus:border-[#70B56A] focus:ring-4 focus:ring-[#70B56A]/15 transition" 
                        value={newOpeningBalance}
                        onChange={(e) => setNewOpeningBalance(e.target.value)}
                        placeholder="e.g. 10000"
                        autoFocus
                      />
                    </div>

                    {/* Quick chips */}
                    <div className="flex flex-wrap gap-1.5">
                      {[1000, 2000, 5000, 10000, 20000, 50000].map(amt => (
                        <button
                          key={amt}
                          type="button"
                          className="px-2.5 py-1 rounded-lg text-xs font-bold bg-[#ECF1EB] hover:bg-[#DCE7DB] text-[#243922] border border-[#DCE7DB] transition active:scale-95"
                          onClick={() => {
                            const cur = parseFloat(newOpeningBalance) || 0;
                            setNewOpeningBalance(String(cur + amt));
                          }}
                        >
                          +{amt.toLocaleString()}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded-lg text-xs font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition"
                        onClick={() => setNewOpeningBalance('')}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Integrated Denominations */
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-2 max-h-[190px] overflow-y-auto pr-1">
                      {(activeCurrency.denominations || []).map(d => {
                        const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                        const numCount = Number(count) || 0;
                        const lineTotal = numCount * Number(d);
                        return (
                          <div 
                            key={d}
                            className={`p-2 rounded-xl border flex items-center justify-between gap-2 transition ${
                              numCount > 0 ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800' : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                            }`}
                          >
                            <span className="font-black text-xs text-zinc-900 dark:text-white min-w-[60px]">{activeCurrency.symbol}{d}</span>
                            <div className="flex items-center gap-1">
                              <button 
                                type="button" 
                                className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                              >
                                -
                              </button>
                              <input 
                                type="number" 
                                min="0"
                                className="w-12 h-6 text-center font-black text-xs bg-white dark:bg-zinc-900 border border-zinc-300 rounded text-zinc-900 dark:text-white"
                                value={count}
                                placeholder="0"
                                onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                              />
                              <button 
                                type="button" 
                                className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                              >
                                +
                              </button>
                            </div>
                            <span className="text-xs font-bold text-zinc-900 dark:text-white min-w-[50px] text-end">{lineTotal.toFixed(0)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="p-2.5 rounded-xl bg-[#ECF1EB] dark:bg-zinc-800 border border-[#DCE7DB] dark:border-zinc-700 flex items-center justify-between">
                      <span className="text-xs font-bold text-[#243922] dark:text-emerald-300">Total Counted:</span>
                      <span className="text-base font-black text-[#243922] dark:text-emerald-300">
                        {currency} {denominationCalculations.grandBaseTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="text-xs text-zinc-500">
                  This sets the drawer opening float for <strong>{formatDisplayDate(fromDate)}</strong>.
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-end gap-2 shrink-0">
                <button 
                  type="button" 
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                  onClick={() => setIsOpeningModalOpen(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition active:scale-95"
                  disabled={isSavingOB}
                  onClick={handleSaveOpeningBalance}
                >
                  {isSavingOB ? 'Saving...' : 'Save Balance'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 7: PROFESSIONAL PRINTABLE FINANCIAL REPORTS MODAL */}
        <FinancialReportsPrintModal
          isOpen={printModal.isOpen}
          onClose={() => setPrintModal(prev => ({ ...prev, isOpen: false }))}
          reportType={printModal.reportType}
          selectedShift={printModal.selectedShift || activeShift}
          selectedRegister={printModal.selectedRegister || registerData}
          dateRange={printModal.dateRange || { from: fromDate, to: toDate }}
          data={{
            ...data,
            sales_payments: salesPayments,
            total_cash_collected: totalCashCollected,
            total_qr_collected: totalQrCollected,
            total_card_collected: totalCardCollected,
            total_collected: totalCollected,
            transactions: filteredTransactions,
            detailedInvoices: data?.invoicesList || filteredTransactions,
            registerSummary: registerData,
            shift: printModal.selectedShift || activeShift,
            currentShift: activeShift
          }}
        />

      </div>
    </Page>
  );
}
